import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  AudioLines,
  Bell,
  BellOff,
  BookOpenText,
  Check,
  CircleUserRound,
  Cookie,
  Delete,
  Gauge,
  Headphones,
  Leaf,
  Lightbulb,
  Languages,
  Layers3,
  LayoutGrid,
  LogIn,
  LogOut,
  Menu,
  MessageSquareText,
  Mic,
  MicOff,
  Moon,
  Play,
  RotateCcw,
  RefreshCw,
  Shuffle,
  Sparkles,
  Star,
  Sun,
  Trash2,
  Volume2,
  X,
} from "lucide-react";
import {
  DILHOCAM_LEARNING_SHELL_V1_ENABLED,
  ECHO_WORKSPACE_V2_ENABLED,
  TASK_FIRST_PREMIUM_UI_ENABLED,
  WEB_TASK_SHELL_V3_ENABLED,
} from "./experienceFlags";

import { getCopy } from "@mobile/app/config/appCopy";
import { AudioReadyBoundary } from "./monetization/AudioReadyBoundary";
import { buildVocabularySpellingAudioRefs } from "@mobile/features/session/alphabetSpelling";
import { isAlphabetSpellingHintAvailable } from "@mobile/features/session/alphabetReleasePolicy";
import {
  buildModeSpecificVocabularyExerciseOrder,
  buildVocabularyExerciseEligibility,
  buildVocabularyExerciseCompletionKey,
  buildVocabularyRunSnapshot,
  resolveVocabularyExerciseRawScore,
} from "@mobile/features/session/vocabularyExerciseMetrics";
import { buildVocabularyExampleTextSegments } from "@mobile/features/session/vocabularyExampleHighlights";
import {
  buildScrambledVocabularyLetterSequence,
  normalizeVocabularyAnswer,
  normalizeVocabularySpelling,
} from "@mobile/features/session/vocabularyOrthography";
import {
  buildScenarioRewardSummaryById,
  getScenarioRewardSummary,
} from "@mobile/features/session/sessionRewardMetrics";
import type { ScenarioPackage, SentenceEntry, TaskSimulationObjective, VocabularyEntry } from "@mobile/runtime/types";
import { sentenceBelongsToIntent } from "@mobile/runtime/intentSentenceMembership";
import type { SpeechAttemptFeedback } from "@mobile/speech/evaluateSpeech";
import { canOfferAssistedProgress } from "@mobile/features/speech/attemptRecoveryPolicy";

import logoUrl from "../../public/assets/wordinarius-echo-product-logo-web.webp";

import {
  LANGUAGE_META,
  SUPPORT_LANGUAGES,
  WEB_COPY,
  directionForSupport,
  speechLocaleForSetup,
  targetLanguagesForSupport,
  type AppRoute,
  type Appearance,
  type LearnerSetup,
  type SupportLanguage,
  type TargetLanguage,
  useAppearance,
  useAppRoute,
  useLearnerSetup,
} from "./appModel";
import {
  getSentenceById,
  getTutorialMissions,
  getTutorialPracticeSentences,
  loadTutorialScenario,
  getTutorialVocabulary,
  resolveTutorialAudioUrl,
} from "./content/tutorialContent";
import { ContentLibraryView } from "./content/ContentLibraryView";
import { domainTitle } from "./content/domainTitle";
import { ContentHeading, ContentHeadingChoice } from "./content/ContentHeading";
import { getWebScenarioHeadingSupport } from "./content/webScenarioOverlay";
import { formatContentChapterHeading } from "@mobile/app/config/contentHeadingLabels";
import { useWebContentLibrary } from "./content/useWebContentLibrary";
import type { LearningLevel, ResolvedWebScenario } from "./content/webContentTypes";
import { playWebAudio, playWebAudioSequence, stopWebAudio } from "./platform/webAudio";
import { playWebFeedbackSound } from "./platform/webFeedbackSound";
import {
  getWebMicrophoneAvailability,
  getWebSpeechAvailability,
  recognizeSpeechOnce,
  WebSpeechError,
  type WebMicrophoneAvailability,
} from "./platform/webSpeech";
import { evaluateBestWebSpeechAttempt } from "./platform/evaluateWebSpeech";
import { describeWebAuthError, useWebAuth } from "./platform/firebaseAuth";
import { callAuthenticatedWordinariusFunction } from "./platform/firebaseFunctions";
import { useWebReminders } from "./platform/webReminders";
import { useWebMonetization } from "./monetization/webMonetization";
import {
  useWebAudioAccess,
  type WebRemoteAudioRequest,
} from "./monetization/webAudioAccess";
import { buildWebAudioUnitRefs } from "./monetization/webAudioUnit";
import { showWebPrivacyOptions, useWebAdConsent } from "./monetization/webConsent";
import { useWebInterstitials } from "./monetization/webInterstitials";
import type { InterstitialBreakEvent } from "@mobile/audio/interstitialBreakPolicy";
import {
  recordMissionResult,
  recordPracticeResult,
  recordVocabularyResult,
  useWebProgress,
} from "./progress/webProgress";
import { type GuidedTrialPhase, useGuidedTrial } from "./tutorialState";
import { ContextGuideOverlay, type WebContextGuideId, useWebContextGuides } from "./contextGuides";
import { nextMissionQueueIndex, shuffleMissionQueue } from "./missionQueue";

const APPEARANCE_OPTIONS: Array<{ value: Appearance; label: string; icon: typeof Moon }> = [
  { value: "atelier", label: "Atelier", icon: Leaf },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

const premiumExperienceClassName = [
  TASK_FIRST_PREMIUM_UI_ENABLED ? "premium-task-first-ui" : "",
  ECHO_WORKSPACE_V2_ENABLED ? "echo-workspace-v2" : "",
  WEB_TASK_SHELL_V3_ENABLED ? "web-task-shell-v3" : "",
  DILHOCAM_LEARNING_SHELL_V1_ENABLED ? "dilhocam-learning-shell-v1" : "",
].filter(Boolean).join(" ");

interface ViewProps {
  scenario: ScenarioPackage;
  setup: LearnerSetup;
  audioAccess?: ReturnType<typeof useWebAudioAccess>;
  onNaturalBreak?: (event: InterstitialBreakEvent) => void;
}

type WebProgressController = ReturnType<typeof useWebProgress>;

interface SpeechAttemptState {
  busy: boolean;
  error: string | null;
  feedback: SpeechAttemptFeedback | null;
}

function useSpeechAttempt(setup: LearnerSetup, scenario: ScenarioPackage) {
  const capture = useRef<AbortController | null>(null);
  const attempt = useRef(0);
  const [state, setState] = useState<SpeechAttemptState>({
    busy: false,
    error: null,
    feedback: null,
  });
  const [attemptCount, setAttemptCount] = useState(0);

  useEffect(() => () => { attempt.current += 1; capture.current?.abort(); }, []);

  const run = async (expectedText: string) => {
    capture.current?.abort();
    const currentAttempt = ++attempt.current;
    const controller = new AbortController();
    capture.current = controller;
    setState({ busy: true, error: null, feedback: null });
    try {
      const result = await recognizeSpeechOnce(speechLocaleForSetup(setup), 12000, controller.signal);
      if (currentAttempt !== attempt.current) return null;
      const feedback = evaluateBestWebSpeechAttempt(
        result,
        expectedText,
        scenario.speechEvaluation,
        setup.supportLanguage,
        setup.targetLanguage,
      );
      setAttemptCount((current) => current + 1);
      setState({ busy: false, error: null, feedback });
      return feedback;
    } catch (error) {
      if (currentAttempt !== attempt.current) return null;
      const code = error instanceof WebSpeechError ? error.code : "speech_failed";
      setState({ busy: false, error: code, feedback: null });
      return null;
    } finally {
      if (capture.current === controller) capture.current = null;
    }
  };

  const clear = () => {
    attempt.current += 1;
    capture.current?.abort();
    capture.current = null;
    setState({ busy: false, error: null, feedback: null });
  };
  const resetAttempts = () => {
    setAttemptCount(0);
    clear();
  };
  return { ...state, attemptCount, run, clear, resetAttempts };
}

function useWebMicrophoneAvailability(): WebMicrophoneAvailability {
  const [availability, setAvailability] = useState<WebMicrophoneAvailability>("unknown");

  useEffect(() => {
    let active = true;
    const refresh = () => {
      void getWebMicrophoneAvailability().then((next) => {
        if (active) setAvailability(next);
      });
    };
    refresh();
    navigator.mediaDevices?.addEventListener?.("devicechange", refresh);
    return () => {
      active = false;
      navigator.mediaDevices?.removeEventListener?.("devicechange", refresh);
    };
  }, []);

  return availability;
}

function describeSpeechError(code: string | null, supportLanguage: SupportLanguage): string | null {
  if (!code) return null;
  const copy = WEB_COPY[supportLanguage];
  if (code === "speech_microphone_not_found") return copy.browserMicrophoneMissing;
  if (code === "speech_microphone_permission_denied") return copy.browserMicrophonePermissionDenied;
  if (code === "speech_microphone_unavailable") return copy.browserMicrophoneUnavailable;
  return getCopy(supportLanguage).practiceSpeechNoMatch;
}

function IconButton(props: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  active?: boolean;
  ariaExpanded?: boolean;
  ariaHasPopup?: "menu";
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-button${props.active ? " is-active" : ""}${props.className ? ` ${props.className}` : ""}`}
      aria-label={props.label}
      aria-expanded={props.ariaExpanded}
      aria-haspopup={props.ariaHasPopup}
      title={props.label}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {props.children}
    </button>
  );
}

function AppearanceOptions(props: {
  appearance: Appearance;
  label: string;
  onAppearance: (appearance: Appearance) => void;
  className?: string;
}) {
  return (
    <div className={`theme-options${props.className ? ` ${props.className}` : ""}`} role="group" aria-label={props.label}>
      {APPEARANCE_OPTIONS.map(({ value, label, icon: Icon }) => (
        <IconButton
          key={value}
          label={label}
          active={props.appearance === value}
          onClick={() => props.onAppearance(value)}
        >
          <Icon aria-hidden="true" />
        </IconButton>
      ))}
    </div>
  );
}

function StandaloneQuickAccess(props: {
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
  onLanguageSettings: () => void;
  profileActive?: boolean;
  setup?: LearnerSetup | null;
  supportLanguage: SupportLanguage;
}) {
  const [open, setOpen] = useState(false);
  const copy = WEB_COPY[props.supportLanguage];
  const mobileCopy = getCopy(props.supportLanguage);
  const languageDetail = props.setup
    ? <span className="language-pair-inline"><LanguageFlag language={props.setup.supportLanguage} /><ArrowRight aria-hidden="true" /><LanguageFlag language={props.setup.targetLanguage} /></span>
    : mobileCopy.quickAccessUnavailableHint;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={`mobile-quick-access-fab standalone-quick-access-fab${open ? " is-open" : ""}`}
        aria-label={open ? mobileCopy.quickAccessCloseLabel : mobileCopy.quickAccessOpenLabel}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
      </button>
      {open ? (
        <div className="mobile-quick-access-layer">
          <button
            type="button"
            className="mobile-quick-access-backdrop"
            aria-label={mobileCopy.quickAccessCloseLabel}
            onClick={() => setOpen(false)}
          />
          <section
            className="mobile-quick-access-panel standalone-quick-access-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="standalone-quick-access-title"
          >
            <header>
              <div>
                <h2 id="standalone-quick-access-title">{mobileCopy.quickAccessTitle}</h2>
                <p>{mobileCopy.quickAccessHint}</p>
              </div>
              <IconButton label={mobileCopy.quickAccessCloseLabel} onClick={() => setOpen(false)}>
                <X aria-hidden="true" />
              </IconButton>
            </header>
            <nav aria-label={mobileCopy.quickAccessTitle}>
              <button
                type="button"
                className={props.profileActive ? "tone-2" : "is-active tone-2"}
                onClick={() => {
                  setOpen(false);
                  props.onLanguageSettings();
                }}
              >
                <Languages aria-hidden="true" />
                <span><strong>{mobileCopy.quickAccessLanguageSettingsLabel}</strong><small>{languageDetail}</small></span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className="tone-1" disabled>
                <Gauge aria-hidden="true" />
                <span><strong>{mobileCopy.setupLevelTitle}</strong><small>{mobileCopy.quickAccessUnavailableHint}</small></span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className="tone-2" disabled>
                <LayoutGrid aria-hidden="true" />
                <span><strong>{mobileCopy.pickDomainTitle}</strong><small>{mobileCopy.quickAccessUnavailableHint}</small></span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className="tone-3" disabled>
                <Layers3 aria-hidden="true" />
                <span><strong>{mobileCopy.pickScenarioTitle}</strong><small>{mobileCopy.quickAccessUnavailableHint}</small></span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className="tone-4" disabled>
                <Shuffle aria-hidden="true" />
                <span><strong>{mobileCopy.quickAccessMixedMissions}</strong><small>{mobileCopy.quickAccessUnavailableHint}</small></span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className={props.profileActive ? "is-active tone-1" : "tone-1"} disabled>
                <CircleUserRound aria-hidden="true" />
                <span><strong>{copy.profile}</strong></span>
                <ArrowRight aria-hidden="true" />
              </button>
            </nav>
            <div className="standalone-quick-appearance">
              <strong>{copy.theme}</strong>
              <AppearanceOptions
                appearance={props.appearance}
                label={copy.theme}
                onAppearance={props.onAppearance}
              />
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

function FeedbackEmblem({ passed }: { passed: boolean }) {
  if (!passed) {
    return <span className="feedback-symbol" aria-hidden="true"><AudioLines /></span>;
  }

  return (
    <div className="feedback-celebration" aria-hidden="true">
      {Array.from({ length: 7 }, (_, index) => (
        <Star key={index} className={`feedback-star feedback-star-${index + 1}`} fill="currentColor" />
      ))}
      <span className="feedback-symbol"><Check /></span>
    </div>
  );
}

function FeedbackDialog(props: {
  feedback: SpeechAttemptFeedback | null;
  closeLabel: string;
  onClose: () => void;
  onNext?: () => void;
  onRetry?: () => void;
  onSkip?: () => void;
  actionHint?: string;
  nextLabel: string;
  retryLabel?: string;
  skipLabel?: string;
  transcriptLabel: string;
}) {
  useEffect(() => {
    if (props.feedback) {
      void playWebFeedbackSound(props.feedback.band);
    }
  }, [props.feedback]);

  if (!props.feedback) {
    return null;
  }
  const bandClass = props.feedback.band.toLowerCase();
  const passed = props.feedback.band === "PASS";
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className={`feedback-dialog feedback-${bandClass}`} role="dialog" aria-modal="true" aria-labelledby="feedback-title">
        <IconButton label={props.closeLabel} onClick={props.onClose} className="dialog-close">
          <X aria-hidden="true" />
        </IconButton>
        <FeedbackEmblem passed={passed} />
        <h2 id="feedback-title">{props.feedback.band.replace("_", " ")}</h2>
        <p>{props.feedback.hint}</p>
        <div className="transcript-row">
          <span>{props.transcriptLabel}</span>
          <strong>{props.feedback.displayRecognizedText || props.feedback.recognizedText}</strong>
        </div>
        {props.onNext || props.onRetry || props.onSkip ? (
          <div className="feedback-action">
            {props.onRetry ? (
              <button type="button" className="primary-button" onClick={props.onRetry}>
                <RotateCcw aria-hidden="true" />
                {props.retryLabel || props.nextLabel}
              </button>
            ) : null}
            {props.onSkip ? (
              <button type="button" className="secondary-button" onClick={props.onSkip}>
                {props.skipLabel || props.nextLabel}
                <ArrowRight aria-hidden="true" />
              </button>
            ) : null}
            {props.onNext ? (
              <button type="button" className="primary-button" onClick={props.onNext}>
                {props.nextLabel}
                <ArrowRight aria-hidden="true" />
              </button>
            ) : null}
            {props.actionHint ? <small>{props.actionHint}</small> : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function AnswerFeedbackDialog(props: {
  state: "correct" | "incorrect" | "give_up" | null;
  visible: boolean;
  closeLabel: string;
  correctLabel: string;
  retryLabel: string;
  nextLabel: string;
  onClose: () => void;
  onNext: () => void;
  revealedAnswer?: string;
  giveUpLabel?: string;
  revealLabel?: string;
  onGiveUp?: () => void;
}) {
  useEffect(() => {
    if (props.state && props.state !== "give_up" && props.visible) {
      void playWebFeedbackSound(props.state);
    }
  }, [props.state, props.visible]);

  if (!props.state || !props.visible) {
    return null;
  }

  const passed = props.state === "correct";
  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        className={`feedback-dialog answer-feedback ${props.state} ${passed ? "feedback-pass" : "feedback-fail"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="answer-feedback-title"
      >
        <IconButton label={props.closeLabel} onClick={props.onClose} className="dialog-close">
          <X aria-hidden="true" />
        </IconButton>
        {props.state === "give_up" ? <span className="completion-mark"><BookOpenText aria-hidden="true" /></span> : <FeedbackEmblem passed={passed} />}
        <h2 id="answer-feedback-title">{props.state === "give_up" ? props.revealLabel : passed ? props.correctLabel : props.retryLabel}</h2>
        {props.state === "give_up" ? <p dir="auto">{props.revealedAnswer}</p> : null}
        {props.onGiveUp && props.state === "incorrect" ? <button type="button" className="secondary-button" onClick={props.onGiveUp}>{props.giveUpLabel}</button> : null}
        {passed || props.state === "give_up" ? (
          <div className="feedback-action">
            <button type="button" className="primary-button" onClick={props.onNext}>
              {props.nextLabel}
              <ArrowRight aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

async function playSentenceAudio(params: {
  sentence: SentenceEntry | VocabularyEntry | null;
  audioAccess?: ReturnType<typeof useWebAudioAccess>;
  remoteRequest?: WebRemoteAudioRequest | null;
}): Promise<boolean> {
  const audioUrl = params.audioAccess ? null : resolveTutorialAudioUrl(params.sentence?.audioRef);
  if (audioUrl) {
    return playWebAudio(audioUrl);
  }
  if (params.audioAccess && params.remoteRequest) {
    return params.audioAccess.requestPlay(params.remoteRequest);
  }
  return false;
}

function AudioButton({
  sentence,
  label,
  audioAccess,
  remoteRequest,
  disabled = false,
  onPlayed,
}: {
  sentence: SentenceEntry | VocabularyEntry | null;
  label: string;
  audioAccess?: ReturnType<typeof useWebAudioAccess>;
  remoteRequest?: WebRemoteAudioRequest | null;
  disabled?: boolean;
  onPlayed?: () => void;
}) {
  const audioUrl = audioAccess ? null : resolveTutorialAudioUrl(sentence?.audioRef);
  const [playing, setPlaying] = useState(false);
  const playbackEpoch = useRef(0);
  useEffect(() => {
    setPlaying(false);
    return () => { playbackEpoch.current += 1; };
  }, [sentence?.audioRef, remoteRequest?.scenarioId, remoteRequest?.unitId]);

  const play = async () => {
    if (disabled || (!audioUrl && (!audioAccess || !remoteRequest)) || playing) {
      return;
    }
    const epoch = ++playbackEpoch.current;
    setPlaying(true);
    try {
      const completed = await playSentenceAudio({ sentence, audioAccess, remoteRequest });
      if (completed && epoch === playbackEpoch.current) onPlayed?.();
    } catch {
      // The playback control remains available for a retry.
    } finally {
      if (epoch === playbackEpoch.current) setPlaying(false);
    }
  };

  return (
    <IconButton
      label={label}
      onClick={() => void play()}
      disabled={disabled || (!audioUrl && (!audioAccess || !remoteRequest)) || playing}
      className="audio-button"
    >
      {playing ? <AudioLines aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
    </IconButton>
  );
}

function buildRemoteAudioRequest(
  scenario: ScenarioPackage,
  entry: SentenceEntry | VocabularyEntry,
  unitType: WebRemoteAudioRequest["unitType"],
  unitId: string,
): WebRemoteAudioRequest | null {
  if (!entry.audioRef) return null;
  return {
    targetLanguage: String(scenario.targetLanguage || entry.language || "").toLowerCase(),
    level: String(scenario.level || "").toLowerCase(),
    domain: scenario.domain,
    scenarioId: scenario.scenarioId,
    unitType,
    unitId,
    audioRef: entry.audioRef,
    audioRefs: buildWebAudioUnitRefs(scenario, unitType, unitId),
  };
}

function buildScenarioRouteAudioRequest(scenario: ScenarioPackage, route: AppRoute): WebRemoteAudioRequest | null {
  if (route === "practice") {
    const sentence = scenario.sentenceLibrary.find((entry) => Boolean(entry.text?.trim() && entry.audioRef));
    return sentence ? buildRemoteAudioRequest(scenario, sentence, "intentHeading", sentence.intent) : null;
  }
  if (route === "vocabulary") {
    const item = scenario.vocabularyLibrary?.find((entry) => Boolean((entry.display || entry.lemma || "").trim() && entry.audioRef));
    return item ? buildRemoteAudioRequest(scenario, item, "scenarioVocabulary", "scenario_vocabulary") : null;
  }
  if (route === "missions") {
    const mission = scenario.taskStructure.simulationObjectives?.[0];
    const sentence = [mission?.systemLeadSentenceId, mission?.sentenceId, mission?.systemReplySentenceId]
      .map((sentenceId) => getSentenceById(scenario, sentenceId))
      .find((entry) => Boolean(entry?.audioRef));
    return sentence ? buildRemoteAudioRequest(scenario, sentence, "scenarioMissions", "scenario_missions") : null;
  }
  return null;
}

function SpeechButton(props: {
  busy: boolean;
  disabled?: boolean;
  label: string;
  microphoneMissingLabel: string;
  unavailableLabel: string;
  onStart: () => void;
}) {
  const supported = getWebSpeechAvailability() === "supported";
  const microphoneAvailability = useWebMicrophoneAvailability();
  const microphoneMissing = supported && microphoneAvailability === "missing";
  return (
    <div className="speech-action">
      <button
        type="button"
        className={`speech-button${props.busy ? " is-listening" : ""}`}
        onClick={props.onStart}
        disabled={!supported || microphoneMissing || props.busy || props.disabled}
        aria-label={props.label}
      >
        {props.busy ? <AudioLines aria-hidden="true" /> : <Mic aria-hidden="true" />}
        <span>{props.label}</span>
      </button>
      {!supported ? <p className="inline-status" role="status">{props.unavailableLabel}</p> : null}
      {microphoneMissing ? (
        <p className="inline-status microphone-status" role="alert">
          <MicOff aria-hidden="true" />
          <span>{props.microphoneMissingLabel}</span>
        </p>
      ) : null}
    </div>
  );
}

function LanguageFlag({ language }: { language: LearnerSetup["targetLanguage"] }) {
  return <img className="language-flag-image" src={LANGUAGE_META[language].flagImage} width={28} height={21} alt="" aria-hidden="true" />;
}

function TranslationDisclosure(props: {
  label: string;
  supportLanguage: LearnerSetup["supportLanguage"];
  targetLanguage: LearnerSetup["targetLanguage"];
  text?: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (!props.text?.trim()) return null;
  return (
    <div className="translation-disclosure">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={props.label}
        title={props.label}
      >
        <LanguageFlag language={open ? props.targetLanguage : props.supportLanguage} />
      </button>
      {open ? <p dir="auto">{props.text}</p> : null}
    </div>
  );
}

function sentenceTranslation(scenario: ScenarioPackage, sentenceId?: string | null): string | null {
  if (!sentenceId) return null;
  return scenario.translationSupport?.sentenceTranslations
    ?.find((entry) => entry.sentenceId === sentenceId)?.translation || null;
}

function vocabularyTranslation(scenario: ScenarioPackage, vocabularyId?: string | null): string | null {
  if (!vocabularyId) return null;
  return scenario.translationSupport?.vocabularyMeanings
    ?.find((entry) => entry.vocabularyId === vocabularyId)?.meanings?.join(" · ") || null;
}

function SetupFlow({ onComplete, onCancel, initialSetup, appearance, onAppearance }: {
  onComplete: (setup: LearnerSetup) => void;
  onCancel?: () => void;
  initialSetup?: LearnerSetup | null;
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
}) {
  const [supportLanguage, setSupportLanguage] = useState<SupportLanguage | null>(null);
  const displayLanguage = supportLanguage || initialSetup?.supportLanguage || "en";
  const copy = getCopy(displayLanguage);
  const webCopy = WEB_COPY[displayLanguage];
  const targetOptions = supportLanguage ? targetLanguagesForSupport(supportLanguage) : [];

  return (
    <main className={`setup-screen ${premiumExperienceClassName}`.trim()}>
      {!onCancel ? (
        <StandaloneQuickAccess
          appearance={appearance}
          onAppearance={onAppearance}
          onLanguageSettings={() => setSupportLanguage(null)}
          setup={null}
          supportLanguage={displayLanguage}
        />
      ) : null}
      <header className="setup-brand">
        {onCancel ? (
          <button type="button" className="back-button setup-exit-button" onClick={onCancel} aria-label={copy.previous}>
            <ArrowLeft aria-hidden="true" />
          </button>
        ) : null}
        <div className="setup-brand-identity">
          <img src={logoUrl} width="72" height="72" alt="" />
          <div>
            <strong lang="en">Wordinarius Echo</strong>
            <span>{webCopy.webAppTitle}</span>
          </div>
        </div>
        <AppearanceOptions
          appearance={appearance}
          className="setup-theme-options"
          label={webCopy.theme}
          onAppearance={onAppearance}
        />
      </header>

      <div className="setup-progress" aria-label="Setup progress">
        <span className="is-complete" />
        <span className={supportLanguage ? "is-complete" : ""} />
      </div>

      {!supportLanguage ? (
        <section className="setup-content" aria-labelledby="support-language-title">
          <div className="setup-heading">
            <span>01</span>
            <h1 id="support-language-title">{copy.setupNativeTitle}</h1>
            <p>{copy.setupNativeHint}</p>
          </div>
          <div className="language-options">
            {SUPPORT_LANGUAGES.map((code) => {
              const language = LANGUAGE_META[code];
              return (
                <button key={code} type="button" className="language-option" onClick={() => setSupportLanguage(code)}>
                  <span className="language-flag" aria-hidden="true"><LanguageFlag language={language.code} /></span>
                  <span><strong>{language.nativeName}</strong><small>{language.englishName}</small></span>
                  <ArrowRight aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="setup-content" aria-labelledby="target-language-title" dir={directionForSupport(supportLanguage)}>
          <button type="button" className="back-button" onClick={() => setSupportLanguage(null)} aria-label="Back">
            <ArrowLeft aria-hidden="true" />
          </button>
          <div className="setup-heading">
            <span>02</span>
            <h1 id="target-language-title">{copy.setupTargetTitle}</h1>
            <p>{copy.setupTargetHint}</p>
          </div>
          <div className="language-options target-options">
            {targetOptions.map((code) => {
              const language = LANGUAGE_META[code];
              return (
                <button
                  key={code}
                  type="button"
                  className="language-option"
                  onClick={() => onComplete({ supportLanguage, targetLanguage: code, englishAccent: "us" })}
                >
                  <span className="language-flag" aria-hidden="true"><LanguageFlag language={language.code} /></span>
                  <span><strong>{language.nativeName}</strong><small>{language.englishName}</small></span>
                  <Play aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}

type NavigationCopyKey = "home" | "practice" | "vocabulary" | "missions" | "mixed";

const NAV_ITEMS: Array<{ route: AppRoute; icon: typeof BookOpenText; copyKey: NavigationCopyKey }> = [
  { route: "home", icon: BookOpenText, copyKey: "home" },
  { route: "practice", icon: MessageSquareText, copyKey: "practice" },
  { route: "vocabulary", icon: Languages, copyKey: "vocabulary" },
  { route: "missions", icon: Sparkles, copyKey: "missions" },
  { route: "mixed", icon: Shuffle, copyKey: "mixed" },
];

function AppNavigation(props: {
  route: AppRoute;
  onRoute: (route: AppRoute) => void;
  onChangeLanguages: () => void;
  onChooseLevel: () => void;
  onChooseDomain: () => void;
  onChangeScenario: () => void;
  onOpenCurrentScenario: () => void;
  setup: LearnerSetup;
  selectedLevel: LearningLevel | null;
  selectedDomain: string | null;
  currentScenarioTitle: string | null;
  learningReady: boolean;
  mixedReady: boolean;
}) {
  const copy = WEB_COPY[props.setup.supportLanguage];
  const mobileCopy = getCopy(props.setup.supportLanguage);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [props.route]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  const navigate = (nextRoute: AppRoute) => {
    setMobileOpen(false);
    props.onRoute(nextRoute);
  };
  return (
    <>
      <aside className="desktop-nav" aria-label="Primary">
        <a className="nav-brand" href="/" aria-label="Wordinarius Echo home">
          <img src={logoUrl} width="48" height="48" alt="" />
          <span lang="en"><strong>Wordinarius</strong><small>Echo</small></span>
        </a>
        <nav>
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.route}
                type="button"
                className={`${props.route === item.route ? "is-active " : ""}${item.route === "mixed" ? (!props.mixedReady ? "needs-content" : "") : (item.route !== "home" && !props.learningReady ? "needs-content" : "")}`}
                onClick={() => props.onRoute(item.route)}
              >
                <Icon aria-hidden="true" />
                <span>{copy[item.copyKey]}</span>
              </button>
            );
          })}
        </nav>
        <div className="desktop-nav-actions">
          <button
            type="button"
            className="desktop-quick-access-button"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
          >
            <Menu aria-hidden="true" />
            <span>{mobileCopy.quickAccessTitle}</span>
          </button>
          <button type="button" className="language-nav" onClick={props.onChangeLanguages}>
            <Languages aria-hidden="true" />
            <span>{copy.resetSetup}</span>
          </button>
          <button type="button" className={props.route === "profile" ? "profile-nav is-active" : "profile-nav"} onClick={() => props.onRoute("profile")}>
            <CircleUserRound aria-hidden="true" />
            <span>{copy.profile}</span>
          </button>
        </div>
      </aside>
      <button
        type="button"
        className={`mobile-quick-access-fab ${props.route === "home" ? "is-home-route" : "is-subroute"}${mobileOpen ? " is-open" : ""}`}
        aria-label={mobileOpen ? mobileCopy.quickAccessCloseLabel : mobileCopy.quickAccessOpenLabel}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((current) => !current)}
      >
        {mobileOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
      </button>
      {mobileOpen ? (
        <div className="mobile-quick-access-layer">
          <button type="button" className="mobile-quick-access-backdrop" aria-label={mobileCopy.quickAccessCloseLabel} onClick={() => setMobileOpen(false)} />
          <section className="mobile-quick-access-panel" role="dialog" aria-modal="true" aria-labelledby="mobile-quick-access-title">
            <header>
              <div>
                <h2 id="mobile-quick-access-title">{mobileCopy.quickAccessTitle}</h2>
                <p>{mobileCopy.quickAccessHint}</p>
              </div>
              <IconButton label={mobileCopy.quickAccessCloseLabel} onClick={() => setMobileOpen(false)}><X aria-hidden="true" /></IconButton>
            </header>
            <nav aria-label={mobileCopy.quickAccessTitle}>
              <button type="button" className="tone-2" onClick={() => { setMobileOpen(false); props.onChangeLanguages(); }}>
                <Languages aria-hidden="true" />
                <span>
                  <strong>{copy.resetSetup}</strong>
                  <small className="language-pair-inline"><LanguageFlag language={props.setup.supportLanguage} /><ArrowRight aria-hidden="true" /><LanguageFlag language={props.setup.targetLanguage} /></small>
                </span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button type="button" className="tone-1" onClick={() => { setMobileOpen(false); props.onChooseLevel(); }}>
                <Gauge aria-hidden="true" />
                <span>
                  <strong>{mobileCopy.setupLevelTitle}</strong>
                  <small>{props.selectedLevel?.toUpperCase() || mobileCopy.quickAccessUnavailableHint}</small>
                </span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tone-2"
                disabled={!props.selectedLevel}
                onClick={() => { setMobileOpen(false); props.onChooseDomain(); }}
              >
                <LayoutGrid aria-hidden="true" />
                <span>
                  <strong>{mobileCopy.pickDomainTitle}</strong>
                  <small>{props.selectedDomain ? domainTitle(props.selectedDomain, props.setup.targetLanguage) : mobileCopy.quickAccessUnavailableHint}</small>
                </span>
                <ArrowRight aria-hidden="true" />
              </button>
              <button
                type="button"
                className="tone-3"
                disabled={!props.selectedLevel || !props.selectedDomain}
                onClick={() => { setMobileOpen(false); props.onChangeScenario(); }}
              >
                <Layers3 aria-hidden="true" />
                <span>
                  <strong>{mobileCopy.pickScenarioTitle}</strong>
                  <small>{props.currentScenarioTitle || mobileCopy.quickAccessUnavailableHint}</small>
                </span>
                <ArrowRight aria-hidden="true" />
              </button>
              {props.learningReady ? (
                <button type="button" className="tone-4" onClick={() => { setMobileOpen(false); props.onOpenCurrentScenario(); }}>
                  <BookOpenText aria-hidden="true" />
                  <span>
                    <strong>{mobileCopy.quickAccessCurrentScenario}</strong>
                    <small>{props.currentScenarioTitle}</small>
                  </span>
                  <ArrowRight aria-hidden="true" />
                </button>
              ) : null}
              {NAV_ITEMS.map((item, index) => {
                const Icon = item.icon;
                const needsContent = item.route === "mixed"
                  ? !props.mixedReady
                  : item.route !== "home" && !props.learningReady;
                return (
                  <button
                    key={item.route}
                    type="button"
                    className={`${props.route === item.route ? "is-active " : ""}tone-${(index % 4) + 1}`}
                    onClick={() => navigate(item.route)}
                  >
                    <Icon aria-hidden="true" />
                    <span><strong>{copy[item.copyKey]}</strong>{needsContent ? <small>{copy.chooseScenarioBeforePractice}</small> : null}</span>
                    <ArrowRight aria-hidden="true" />
                  </button>
                );
              })}
              <button type="button" className={props.route === "profile" ? "is-active tone-1" : "tone-1"} onClick={() => navigate("profile")}>
                <CircleUserRound aria-hidden="true" />
                <span><strong>{copy.profile}</strong></span>
                <ArrowRight aria-hidden="true" />
              </button>
            </nav>
          </section>
        </div>
      ) : null}
    </>
  );
}

function AppHeader(props: {
  setup: LearnerSetup;
  route: AppRoute;
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
  onBack: () => void;
  onMixed: () => void;
  onChangeLanguages: () => void;
  learningReady: boolean;
  mixedReady: boolean;
  showAppearance: boolean;
}) {
  const [appearanceMenuOpen, setAppearanceMenuOpen] = useState(false);
  const target = LANGUAGE_META[props.setup.targetLanguage];
  const copy = WEB_COPY[props.setup.supportLanguage];
  const routeLabel = copy[props.route];
  const activeAppearance = APPEARANCE_OPTIONS.find(({ value }) => value === props.appearance) || APPEARANCE_OPTIONS[0];
  const ActiveAppearanceIcon = activeAppearance.icon;

  useEffect(() => {
    if (!props.showAppearance) {
      setAppearanceMenuOpen(false);
    }
  }, [props.showAppearance]);

  const chooseAppearance = (appearance: Appearance) => {
    props.onAppearance(appearance);
    setAppearanceMenuOpen(false);
  };

  return (
    <header className={`app-header route-${props.route}`}>
      <IconButton label={copy.mixed} onClick={props.onMixed} className={`mobile-menu-button${props.mixedReady ? "" : " needs-content"}`}>
        <Shuffle aria-hidden="true" />
      </IconButton>
      <div className="app-header-context">
        {props.route !== "home" ? (
          <IconButton label={copy.home} onClick={props.onBack} className="workspace-back-button">
            <ArrowLeft aria-hidden="true" />
          </IconButton>
        ) : null}
        <div className="app-header-location">
          <button type="button" className="pair-indicator" onClick={props.onChangeLanguages} aria-label={copy.resetSetup}>
            <LanguageFlag language={props.setup.supportLanguage} />
            <ArrowRight aria-hidden="true" />
            <LanguageFlag language={props.setup.targetLanguage} />
            <strong>{target.nativeName}</strong>
          </button>
          {props.route !== "home" ? <span className="route-context-label">{routeLabel}</span> : null}
        </div>
      </div>
      {props.showAppearance ? (
        <div className="appearance-control">
          <AppearanceOptions
            appearance={props.appearance}
            className="appearance-desktop-options"
            label={copy.theme}
            onAppearance={props.onAppearance}
          />
          <IconButton
            label={copy.theme}
            active
            ariaExpanded={appearanceMenuOpen}
            ariaHasPopup="menu"
            className="appearance-mobile-trigger"
            onClick={() => setAppearanceMenuOpen((open) => !open)}
          >
            <ActiveAppearanceIcon aria-hidden="true" />
          </IconButton>
          {appearanceMenuOpen ? (
            <AppearanceOptions
              appearance={props.appearance}
              className="appearance-mobile-menu"
              label={copy.theme}
              onAppearance={chooseAppearance}
            />
          ) : null}
        </div>
      ) : <span className="app-header-spacer" aria-hidden="true" />}
    </header>
  );
}

function HomeView(props: ViewProps & {
  onRoute: (route: AppRoute) => void;
  onChangeScenario: () => void;
  progress?: WebProgressController;
  progressByLevel?: Record<LearningLevel, WebProgressController>;
  rewardedAccess: boolean;
}) {
  const copy = WEB_COPY[props.setup.supportLanguage];
  const mobileCopy = getCopy(props.setup.supportLanguage);
  const rewardSummary = getScenarioRewardSummary(buildScenarioRewardSummaryById(props.progress?.state || {
    passedPracticeItemIdsByScenario: {},
    completedVocabularyExerciseKeysByScenario: {},
    vocabularyExerciseScoresByScenario: {},
    missionMetricsByScenario: {},
  }), props.scenario.scenarioId);
  const modes: Array<{ route: AppRoute; index: string; title: string; detail: string; xp: number; coins: number; icon: typeof MessageSquareText; tone: string }> = [
    { route: "practice", index: "01", title: copy.practice, detail: mobileCopy.practiceHint, xp: rewardSummary.practiceXp, coins: rewardSummary.practiceDiamonds, icon: MessageSquareText, tone: "cyan" },
    { route: "vocabulary", index: "02", title: copy.vocabulary, detail: mobileCopy.pickVocabularyExercisesHint, xp: rewardSummary.vocabularyXp, coins: rewardSummary.vocabularyDiamonds, icon: Languages, tone: "violet" },
    { route: "missions", index: "03", title: copy.missions, detail: mobileCopy.dialogueHint, xp: rewardSummary.missionXp, coins: rewardSummary.missionDiamonds, icon: Sparkles, tone: "green" },
    { route: "mixed", index: "04", title: copy.mixed, detail: mobileCopy.mixedMissionsHint, xp: rewardSummary.missionXp, coins: rewardSummary.missionDiamonds, icon: Shuffle, tone: "amber" },
  ];
  return (
    <section className="view home-view" aria-labelledby="home-title">
      <div className="view-heading">
        <span>{props.scenario.level.toUpperCase()} · <ContentHeading title={domainTitle(props.scenario.domain, props.setup.targetLanguage)} supportTitle={domainTitle(props.scenario.domain, props.setup.supportLanguage)} setup={props.setup} /></span>
        <h1 id="home-title"><ContentHeading title={props.scenario.title} supportTitle={getWebScenarioHeadingSupport(props.scenario, props.setup.supportLanguage)?.title} setup={props.setup} /></h1>
        <p>{props.scenario.microSkill?.replaceAll("_", " ")}</p>
        {props.progress ? (
          <div className="scenario-progress-summary" aria-label={`${props.progress.state.scenarioProgressById[props.scenario.scenarioId] || 0}%`}>
            <span>{props.progress.state.scenarioProgressById[props.scenario.scenarioId] || 0}%</span>
            <div className="progress-track"><span style={{ width: `${props.progress.state.scenarioProgressById[props.scenario.scenarioId] || 0}%` }} /></div>
          </div>
        ) : null}
        <button type="button" className="text-button change-scenario" onClick={props.onChangeScenario}>
          <Layers3 aria-hidden="true" />
          {mobileCopy.changeScenario}
        </button>
      </div>
      <div className="mode-grid">
        {modes.map((mode) => {
          const Icon = mode.icon;
          return (
            <button
              key={mode.route}
              type="button"
              className={`mode-card tone-${mode.tone}${mode.route === "practice" ? " is-primary" : ""}`}
              onClick={() => props.onRoute(mode.route)}
            >
              <span className="mode-index">{mode.index}</span>
              <Icon aria-hidden="true" />
              <span className="mode-copy"><strong>{mode.title}</strong><em>{mode.xp} XP · {mode.coins} {mobileCopy.diamondsLabel}</em>{props.rewardedAccess && mode.route !== "practice" ? <b>{mobileCopy.practiceLockedShortAdLabel}</b> : null}</span>
              <ArrowRight aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </section>
  );
}

function PracticeView({
  scenario,
  setup,
  audioAccess,
  onTutorialComplete,
  progress,
  onNaturalBreak,
  onOpenMissions,
}: ViewProps & { onTutorialComplete?: () => void; progress?: WebProgressController; onOpenMissions?: () => void }) {
  const practiceSections = useMemo(() => (
    onTutorialComplete
      ? []
      : (scenario.intentSystem || []).filter((intent) => (
        scenario.sentenceLibrary.some((sentence) => (
          sentenceBelongsToIntent(sentence, intent.intentId) && Boolean(sentence.text?.trim())
        ))
      ))
  ), [onTutorialComplete, scenario]);
  const [selectedIntentId, setSelectedIntentId] = useState(
    practiceSections[0]?.intentId || scenario.sentenceLibrary[0]?.intent || "",
  );
  const sentences = useMemo(() => (
    onTutorialComplete
      ? getTutorialPracticeSentences(scenario)
      : scenario.sentenceLibrary.filter((sentence) => (
        Boolean(sentence.text?.trim())
        && (!selectedIntentId || sentenceBelongsToIntent(sentence, selectedIntentId))
      ))
  ), [onTutorialComplete, scenario, selectedIntentId]);
  const [index, setIndex] = useState(0);
  const [chapterComplete, setChapterComplete] = useState(false);
  const speech = useSpeechAttempt(setup, scenario);
  const copy = WEB_COPY[setup.supportLanguage];
  const mobileCopy = getCopy(setup.supportLanguage);
  const sentence = sentences[index] || null;
  const intent = scenario.intentSystem?.find((entry) => entry.intentId === selectedIntentId);
  const headingSupport = getWebScenarioHeadingSupport(scenario, setup.supportLanguage);
  const chapterNumber = Math.max(0, scenario.intentSystem?.findIndex((entry) => entry.intentId === selectedIntentId) ?? 0) + 1;
  const supportIntentTitle = intent ? headingSupport?.intentTitlesById[intent.intentId] : undefined;
  const assistedProgress = Boolean(
    speech.feedback
    && speech.feedback.band !== "PASS"
    && canOfferAssistedProgress({
      attemptCount: speech.attemptCount,
      resolved: false,
    }),
  );

  useEffect(() => speech.resetAttempts(), [index]);
  useEffect(() => {
    const firstIntentId = practiceSections[0]?.intentId || scenario.sentenceLibrary[0]?.intent || "";
    setSelectedIntentId(firstIntentId);
    setIndex(0);
    setChapterComplete(false);
  }, [practiceSections, scenario]);

  if (!sentence) {
    return <p className="empty-state">{mobileCopy.practiceNoSentenceState}</p>;
  }

  const moveTo = (nextIndex: number) => {
    if (progress && speech.feedback) {
      progress.update((current) => recordPracticeResult(
        current,
        scenario.scenarioId,
        sentence.sentenceId,
        speech.feedback?.band === "PASS",
        sentences.length,
      ));
    }
    speech.resetAttempts();
    if (onTutorialComplete) {
      onTutorialComplete();
      return;
    }
    if (nextIndex >= sentences.length) {
      onNaturalBreak?.("practiceHeadingComplete");
      setChapterComplete(true);
      return;
    }
    setIndex(Math.max(0, nextIndex));
  };
  const next = () => moveTo(index + 1);
  const selectedSectionIndex = practiceSections.findIndex((section) => section.intentId === selectedIntentId);
  const nextSection = selectedSectionIndex >= 0 ? practiceSections[selectedSectionIndex + 1] : undefined;
  const selectPracticeSection = (section: (typeof practiceSections)[number]) => {
    const firstSentence = scenario.sentenceLibrary.find((candidate) => (
      sentenceBelongsToIntent(candidate, section.intentId) && Boolean(candidate.audioRef)
    ));
    const select = () => {
      setSelectedIntentId(section.intentId);
      setIndex(0);
      setChapterComplete(false);
      speech.resetAttempts();
    };
    const request = firstSentence
      ? buildRemoteAudioRequest(scenario, firstSentence, "intentHeading", section.intentId)
      : null;
    if (!request || !audioAccess) {
      select();
    } else {
      void audioAccess.prepareUnit(request).then((granted) => granted && select());
    }
  };

  return (
    <section className="view lesson-view" aria-labelledby="practice-title">
      <div className="lesson-topline">
        <div>
          <span>{copy.practice}</span>
          <h1 id="practice-title"><ContentHeading
            title={intent ? formatContentChapterHeading(setup.targetLanguage, chapterNumber, intent.title || scenario.title) : scenario.title}
            supportTitle={supportIntentTitle ? formatContentChapterHeading(setup.supportLanguage, chapterNumber, supportIntentTitle) : headingSupport?.title}
            setup={setup}
          /></h1>
          {intent?.description ? <small>{intent.description}</small> : null}
        </div>
        <div className="lesson-position">
          <strong>{index + 1}/{sentences.length}</strong>
        </div>
      </div>
      {!onTutorialComplete && practiceSections.length > 1 ? (
        <div className="practice-chapter-tabs" role="group" aria-label={copy.practice}>
          {practiceSections.map((section, sectionIndex) => (
            <ContentHeadingChoice
              key={section.intentId}
              title={section.title || ""}
              supportTitle={headingSupport?.intentTitlesById[section.intentId]}
              setup={setup}
              buttonProps={{ "aria-pressed": selectedIntentId === section.intentId, className: selectedIntentId === section.intentId ? "is-active" : "", onClick: () => selectPracticeSection(section) }}
            >
              {(title, language) => <>
              <div className="practice-chapter-choice-meta">
                <span>{String(sectionIndex + 1).padStart(2, "0")}</span>
                <Check className="practice-chapter-selected-mark" aria-hidden="true" size={20} />
              </div>
              <strong lang={language} dir="auto">{title}</strong>
              </>}
            </ContentHeadingChoice>
          ))}
        </div>
      ) : null}
      {chapterComplete ? (
        <section className="lesson-completion" role="status" aria-live="polite">
          <span className="completion-mark"><Check aria-hidden="true" /></span>
          <h2>{mobileCopy.practiceChapterCompleteTitle}</h2>
          <p>{nextSection ? mobileCopy.practiceChapterCompleteNextHint : mobileCopy.practiceChapterCompleteFinalHint}</p>
          <div className="completion-actions">
            <button type="button" className="secondary-button" onClick={() => { setIndex(0); setChapterComplete(false); }}>
              <RotateCcw aria-hidden="true" />{mobileCopy.practiceReplayChapterAction}
            </button>
            {nextSection ? (
              <button type="button" className="primary-button" onClick={() => selectPracticeSection(nextSection)}>
                {mobileCopy.practiceNextChapterAction}<ArrowRight aria-hidden="true" />
              </button>
            ) : onOpenMissions ? (
              <button type="button" className="primary-button" onClick={onOpenMissions}>
                {mobileCopy.practiceGoToMissionsAction}<ArrowRight aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </section>
      ) : (
        <>
      <div className="progress-track"><span style={{ width: `${((index + 1) / sentences.length) * 100}%` }} /></div>
      <div className="lesson-stage sentence-stage" dir="auto">
        <span className="stage-label">{mobileCopy.practiceTargetSentenceLabel}</span>
        <AudioButton
          sentence={sentence}
          label={mobileCopy.listen}
          disabled={speech.busy}
          audioAccess={audioAccess}
          remoteRequest={buildRemoteAudioRequest(scenario, sentence, "intentHeading", selectedIntentId || sentence.intent)}
        />
        <p>{sentence.text}</p>
        <TranslationDisclosure
          label={copy.translate}
          supportLanguage={setup.supportLanguage}
          targetLanguage={setup.targetLanguage}
          text={sentenceTranslation(scenario, sentence.sentenceId)}
        />
        {!onTutorialComplete ? (
          <div className="lesson-navigation sentence-navigation" aria-label={copy.practice}>
            <IconButton label={mobileCopy.previous} disabled={index === 0} onClick={() => moveTo(index - 1)}><ArrowLeft aria-hidden="true" /></IconButton>
            <IconButton label={mobileCopy.practiceNextSentence} onClick={() => moveTo(index + 1)}><ArrowRight aria-hidden="true" /></IconButton>
          </div>
        ) : null}
      </div>
      <SpeechButton
        busy={speech.busy}
        label={speech.busy ? mobileCopy.practiceListening : mobileCopy.practiceTryVoice}
        microphoneMissingLabel={copy.browserMicrophoneMissing}
        unavailableLabel={copy.browserSpeechUnavailable}
        onStart={() => void speech.run(sentence.text)}
      />
      {speech.error ? <p className="error-status" role="alert">{describeSpeechError(speech.error, setup.supportLanguage)}</p> : null}
      <FeedbackDialog
        feedback={speech.feedback}
        closeLabel={copy.close}
        onClose={speech.clear}
        onNext={speech.feedback?.band === "PASS" || assistedProgress ? next : undefined}
        actionHint={assistedProgress ? mobileCopy.assistedProgressHint : undefined}
        nextLabel={assistedProgress ? mobileCopy.assistedProgressAction : copy.next}
        transcriptLabel={copy.transcript}
      />
        </>
      )}
    </section>
  );
}

type VocabularyMode = "listen_repeat" | "unscramble" | "listen_write";

function SpellingHint({ term, setup, disabled }: { term: string; setup: LearnerSetup; disabled: boolean }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const generation = useRef(0);
  const refs = useMemo(() => buildVocabularySpellingAudioRefs(term, setup.targetLanguage), [term, setup.targetLanguage]);
  const copy = getCopy(setup.supportLanguage);
  useEffect(() => {
    setBusy(false);
    setFailed(false);
    return () => { generation.current += 1; stopWebAudio(); };
  }, [term, setup.targetLanguage]);
  const play = async () => {
    const attempt = ++generation.current;
    setBusy(true);
    setFailed(false);
    try {
      const { default: alphabetAudioUrls } = await import("./platform/alphabetAudioUrls.generated");
      if (attempt !== generation.current) return;
      await playWebAudioSequence(refs.map((ref) => alphabetAudioUrls[ref]));
    } catch {
      if (attempt === generation.current) setFailed(true);
    } finally {
      if (attempt === generation.current) setBusy(false);
    }
  };
  return <>
    <button type="button" className="spelling-hint-button" disabled={disabled || busy || refs.length === 0} aria-busy={busy} onClick={() => void play()}>
      <Lightbulb aria-hidden="true" />{copy.vocabularySpellingHintAction}
    </button>
    {failed ? <p className="error-status" role="alert">{copy.audioError}</p> : null}
  </>;
}

function VocabularyView({
  scenario,
  setup,
  audioAccess,
  forcedMode,
  onTutorialComplete,
  progress,
  onNaturalBreak,
}: ViewProps & { forcedMode?: VocabularyMode; onTutorialComplete?: () => void; progress?: WebProgressController }) {
  const vocabularyItems = useMemo(() => (
    onTutorialComplete
      ? getTutorialVocabulary(scenario)
      : (scenario.vocabularyLibrary || []).filter((entry) => Boolean((entry.display || entry.lemma || "").trim()))
  ), [onTutorialComplete, scenario]);
  const [index, setIndex] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [mode, setMode] = useState<VocabularyMode>("listen_repeat");
  const [started, setStarted] = useState(Boolean(onTutorialComplete));
  const [runSnapshot, setRunSnapshot] = useState<ReturnType<typeof buildVocabularyRunSnapshot> | null>(null);
  const [passedThisRun, setPassedThisRun] = useState<string[]>([]);
  const answerEpoch = useRef(0);
  const requestedMode = forcedMode || mode;
  const [writtenAnswer, setWrittenAnswer] = useState("");
  const [selectedLetterIndexes, setSelectedLetterIndexes] = useState<number[]>([]);
  const [answerState, setAnswerState] = useState<"correct" | "incorrect" | "give_up" | null>(null);
  const [answerFeedbackVisible, setAnswerFeedbackVisible] = useState(false);
  const [hintStage, setHintStage] = useState<0 | 1 | 2>(0);
  const [hintAudioError, setHintAudioError] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [failuresById, setFailuresById] = useState<Record<string, number>>({});
  const lastScoredSpeech = useRef<SpeechAttemptFeedback | null>(null);
  const speech = useSpeechAttempt(setup, scenario);
  const copy = WEB_COPY[setup.supportLanguage];
  const mobileCopy = getCopy(setup.supportLanguage);
  const vocabularyEligibility = useMemo(
    () => buildVocabularyExerciseEligibility(vocabularyItems),
    [vocabularyItems],
  );
  const activeMode: VocabularyMode = requestedMode === "unscramble"
    && vocabularyEligibility.unscrambleIds.length === 0
    ? "listen_repeat"
    : requestedMode;
  const vocabularyItemById = useMemo(() => new Map(
    vocabularyItems.map((entry) => [String(entry.vocabularyId || entry.display || entry.lemma || "").trim(), entry]),
  ), [vocabularyItems]);
  const availableModes = useMemo(() => ([
    ["listen_repeat", copy.listenRepeat, Headphones],
    ...(vocabularyEligibility.unscrambleIds.length > 0
      ? [["unscramble", copy.unscramble, Shuffle] as const]
      : []),
    ["listen_write", copy.listenWrite, BookOpenText],
  ] as const), [copy.listenRepeat, copy.listenWrite, copy.unscramble, vocabularyEligibility.unscrambleIds.length]);
  const orderedItems = useMemo(() => {
    const itemIds = activeMode === "listen_repeat"
      ? vocabularyEligibility.listenRepeatIds
      : activeMode === "unscramble"
        ? vocabularyEligibility.unscrambleIds
        : vocabularyEligibility.listenWriteIds;
    return buildModeSpecificVocabularyExerciseOrder(itemIds, activeMode, scenario.scenarioId)
      .map((itemId) => vocabularyItemById.get(itemId))
      .filter((entry): entry is VocabularyEntry => Boolean(entry));
  }, [activeMode, scenario.scenarioId, vocabularyEligibility, vocabularyItemById]);
  const activeItems = useMemo(() => runSnapshot
    ? runSnapshot.itemIds.map((id) => vocabularyItemById.get(id)).filter((entry): entry is VocabularyEntry => Boolean(entry))
    : orderedItems, [orderedItems, runSnapshot, vocabularyItemById]);
  const completedKeys = useMemo(() => new Set(progress?.state.completedVocabularyExerciseKeysByScenario[scenario.scenarioId] || []), [progress?.state.completedVocabularyExerciseKeysByScenario, scenario.scenarioId]);
  const item = activeItems[index] || orderedItems[0] || null;
  const term = item?.display || item?.lemma || "";
  const letters = useMemo(() => {
    return buildScrambledVocabularyLetterSequence(
      term,
      setup.targetLanguage,
      `${scenario.scenarioId}:${activeMode}:${item?.vocabularyId || index}`,
    );
  }, [activeMode, index, item?.vocabularyId, scenario.scenarioId, term, setup.targetLanguage]);
  const selectedAnswer = selectedLetterIndexes.map((letterIndex) => letters[letterIndex]).join("");
  const examples = useMemo(() => (item?.sentenceIds || [])
    .map((id) => getSentenceById(scenario, id)).filter((entry): entry is SentenceEntry => Boolean(entry)), [item, scenario]);

  useEffect(() => {
    if (!started || onTutorialComplete || activeMode !== "listen_repeat" || !item || speech.feedback?.band !== "PASS" || lastScoredSpeech.current === speech.feedback) return;
    lastScoredSpeech.current = speech.feedback;
    setPassedThisRun((current) => current.includes(item.vocabularyId) ? current : [...current, item.vocabularyId]);
    progress?.update((current) => recordVocabularyResult(current, scenario.scenarioId, item.vocabularyId, activeMode, 10));
  }, [activeMode, item, onTutorialComplete, progress, scenario.scenarioId, speech.feedback, started]);

  const resetAnswer = () => {
    answerEpoch.current += 1;
    speech.clear();
    setWrittenAnswer("");
    setSelectedLetterIndexes([]);
    setAnswerState(null);
    setAnswerFeedbackVisible(false);
    setHintStage(0);
    setHintAudioError(false);
    setExamplesOpen(false);
    stopWebAudio();
  };

  useEffect(() => {
    resetAnswer();
    return () => { answerEpoch.current += 1; stopWebAudio(); };
  }, [index, activeMode, scenario.scenarioId]);
  useEffect(() => {
    setIndex(0);
    setSessionComplete(false);
    setStarted(Boolean(onTutorialComplete));
    setRunSnapshot(null);
    setPassedThisRun([]);
  }, [activeMode, scenario.scenarioId]);

  if (!item) {
    return <p className="empty-state">{mobileCopy.genericError}</p>;
  }

  const begin = (replay = false) => {
    if (progress && !progress.ready) return;
    const snapshot = buildVocabularyRunSnapshot(orderedItems.map((entry) => entry.vocabularyId), activeMode, completedKeys, replay);
    resetAnswer();
    setRunSnapshot(snapshot);
    setPassedThisRun([]);
    setIndex(snapshot.initialIndex);
    setStarted(true);
    setSessionComplete(snapshot.itemIds.length === 0);
  };
  const repeatItemCleared = (id: string) => passedThisRun.includes(id)
    || (!runSnapshot?.replay && completedKeys.has(buildVocabularyExerciseCompletionKey("listen_repeat", id)));
  const repeatComplete = activeMode === "listen_repeat" && orderedItems.every((entry) => repeatItemCleared(entry.vocabularyId));
  const position = activeMode === "listen_repeat" ? index + 1 : Math.min(orderedItems.length, (runSnapshot?.completedBeforeRun || 0) + index + 1);

  const next = () => {
    resetAnswer();
    if (onTutorialComplete) {
      onTutorialComplete();
      return;
    }
    if (activeMode === "listen_repeat" && !repeatComplete) {
      const nextIndex = activeItems.findIndex((entry, entryIndex) => entryIndex > index && !repeatItemCleared(entry.vocabularyId));
      const earlierIndex = activeItems.findIndex((entry) => !repeatItemCleared(entry.vocabularyId));
      setIndex(nextIndex >= 0 ? nextIndex : Math.max(0, earlierIndex));
      return;
    }
    if (repeatComplete || index === activeItems.length - 1) {
      onNaturalBreak?.("vocabularySessionComplete");
      setSessionComplete(true);
      return;
    }
    setIndex((current) => current + 1);
  };
  const checkTextAnswer = (answer: string) => {
    const normalize = activeMode === "listen_write" ? normalizeVocabularyAnswer : normalizeVocabularySpelling;
    const correct = normalize(answer, setup.targetLanguage) === normalize(term, setup.targetLanguage);
    setAnswerState(correct ? "correct" : "incorrect");
    if (correct && progress && !onTutorialComplete) {
      progress.update((current) => recordVocabularyResult(current, scenario.scenarioId, item.vocabularyId, activeMode, resolveVocabularyExerciseRawScore(activeMode, hintStage)));
    }
    setFailuresById((current) => ({ ...current, [item.vocabularyId]: correct ? 0 : (current[item.vocabularyId] || 0) + 1 }));
    setAnswerFeedbackVisible(true);
  };
  const resolvedAnswer = answerState === "correct" || answerState === "give_up";
  const dismissedCorrectFeedback = resolvedAnswer && !answerFeedbackVisible;
  const dismissAnswerFeedback = () => {
    setAnswerFeedbackVisible(false);
    if (answerState === "incorrect") setAnswerState(null);
  };
  const playUnscrambleHint = async () => {
    const epoch = answerEpoch.current;
    setHintStage(2);
    setHintAudioError(false);
    try {
      await playSentenceAudio({ sentence: item, audioAccess, remoteRequest: buildRemoteAudioRequest(scenario, item, "scenarioVocabulary", "scenario_vocabulary") });
    } catch {
      if (epoch === answerEpoch.current) setHintAudioError(true);
    }
  };

  return (
    <section className="view lesson-view" aria-labelledby="vocabulary-title">
      <div className="lesson-topline">
        <div><span>{copy.vocabulary}</span><h1 id="vocabulary-title"><ContentHeading title={scenario.title} supportTitle={getWebScenarioHeadingSupport(scenario, setup.supportLanguage)?.title} setup={setup} /></h1></div>
        {started ? <strong>{position}/{orderedItems.length}</strong> : null}
      </div>
      {!forcedMode ? <div className="segmented-control" role="tablist" aria-label={copy.vocabulary}>
        {availableModes.map(([value, label, Icon]) => (
          <button key={value} type="button" role="tab" aria-selected={activeMode === value} className={activeMode === value ? "is-active" : ""} onClick={() => { setMode(value); setIndex(0); }}>
            <Icon aria-hidden="true" /><span>{label}</span>
          </button>
        ))}
      </div> : null}
      {!started ? (
        <section className="vocabulary-session-start" aria-label={mobileCopy.vocabularyStartAction}>
          <h2>{availableModes.find(([value]) => value === activeMode)?.[1]}</h2>
          <p><strong>{activeMode === "listen_repeat" ? orderedItems.length : buildVocabularyRunSnapshot(orderedItems.map((entry) => entry.vocabularyId), activeMode, completedKeys).itemIds.length || orderedItems.length}</strong> {mobileCopy.modeStatsWords}</p>
          <button type="button" className="primary-button" disabled={Boolean(progress && !progress.ready)} onClick={() => begin()}><Play aria-hidden="true" />{mobileCopy.vocabularyStartAction}</button>
        </section>
      ) : sessionComplete ? (
        <section className="lesson-completion" role="status" aria-live="polite">
          <span className="completion-mark"><Check aria-hidden="true" /></span>
          <h2>{copy.vocabularyComplete}</h2>
          <p>{copy.vocabularyCompleteHint}</p>
          <button type="button" className="primary-button" onClick={() => begin(true)}>
            <RotateCcw aria-hidden="true" />{copy.vocabularyReplay}
          </button>
        </section>
      ) : (
        <>
      <div className={`lesson-stage vocabulary-stage ${activeMode !== "listen_repeat" ? "vocabulary-audio-stage" : ""}`} dir="auto" data-vocabulary-id={item.vocabularyId}>
        {activeMode !== "unscramble" ? <AudioButton
          sentence={item}
          label={mobileCopy.listen}
          disabled={speech.busy}
          audioAccess={audioAccess}
          remoteRequest={buildRemoteAudioRequest(scenario, item, "scenarioVocabulary", "scenario_vocabulary")}
        /> : null}
        {activeMode !== "listen_repeat" ? <strong className="vocabulary-listen-label">{activeMode === "unscramble" ? mobileCopy.vocabularyExerciseUnscrambleTitle : mobileCopy.vocabularyListenAction}</strong> : null}
        {activeMode === "listen_repeat" ? (
          <>
            <p>{term}</p>
            {item.notes ? <small>{item.notes}</small> : null}
            <TranslationDisclosure
              label={copy.translate}
              supportLanguage={setup.supportLanguage}
              targetLanguage={setup.targetLanguage}
              text={vocabularyTranslation(scenario, item.vocabularyId)}
            />
            <button type="button" className="secondary-button" aria-expanded={examplesOpen} onClick={() => setExamplesOpen((open) => !open)}>
              <BookOpenText aria-hidden="true" />{examplesOpen ? mobileCopy.vocabularyHideExamplesAction : mobileCopy.vocabularyExamplesAction}
            </button>
          </>
        ) : null}
      </div>

      {activeMode === "listen_repeat" && examplesOpen ? <section className="vocabulary-examples" aria-label={mobileCopy.vocabularyExamplesLabel}>
        <h2>{mobileCopy.vocabularyExamplesLabel}</h2>
        {examples.length === 0 ? <p>{mobileCopy.vocabularyNoExamplesState}</p> : examples.map((example) => <article className="vocabulary-example" key={example.sentenceId}>
          <div className="vocabulary-example-content" dir="auto">
            <p>{buildVocabularyExampleTextSegments(example.text, term, setup.targetLanguage).map((segment, index) => segment.highlighted ? <mark key={index}>{segment.text}</mark> : <span key={index}>{segment.text}</span>)}</p>
            <TranslationDisclosure label={copy.translate} supportLanguage={setup.supportLanguage} targetLanguage={setup.targetLanguage} text={sentenceTranslation(scenario, example.sentenceId)} />
          </div>
          <AudioButton sentence={example} label={mobileCopy.listen} disabled={speech.busy} audioAccess={audioAccess} remoteRequest={buildRemoteAudioRequest(scenario, example, "scenarioVocabulary", "scenario_vocabulary")} />
        </article>)}
      </section> : null}

      {!onTutorialComplete ? (
        <div className="lesson-navigation vocabulary-navigation" aria-label={copy.vocabulary}>
          <IconButton label={mobileCopy.previous} disabled={index === 0} onClick={() => setIndex((current) => Math.max(0, current - 1))}><ArrowLeft aria-hidden="true" /></IconButton>
          <strong>{position}/{orderedItems.length}</strong>
          <IconButton label={copy.next} disabled={index >= activeItems.length - 1} onClick={() => setIndex((current) => Math.min(activeItems.length - 1, current + 1))}><ArrowRight aria-hidden="true" /></IconButton>
        </div>
      ) : null}

      {activeMode === "listen_repeat" ? (
        <SpeechButton
          busy={speech.busy}
          label={speech.busy ? mobileCopy.practiceListening : mobileCopy.practiceTryVoice}
          microphoneMissingLabel={copy.browserMicrophoneMissing}
          unavailableLabel={copy.browserSpeechUnavailable}
          onStart={() => void speech.run(term)}
        />
      ) : null}
      {activeMode === "listen_repeat" && speech.error ? (
        <p className="error-status" role="alert">{describeSpeechError(speech.error, setup.supportLanguage)}</p>
      ) : null}

      {activeMode === "unscramble" ? (
        <div className="exercise-panel">
          <div className="answer-line" dir="auto">{selectedAnswer || "\u00a0"}</div>
          <div className="letter-grid" dir="auto">
            {letters.map((letter, letterIndex) => (
              <button
                key={`${letter}_${letterIndex}`}
                type="button"
                disabled={resolvedAnswer || selectedLetterIndexes.includes(letterIndex)}
                onClick={() => setSelectedLetterIndexes((current) => [...current, letterIndex])}
              >
                {letter.toLocaleUpperCase(LANGUAGE_META[setup.targetLanguage].locale)}
              </button>
            ))}
          </div>
          <div className="exercise-actions">
            <IconButton label={mobileCopy.vocabularyClearAction} onClick={() => { setSelectedLetterIndexes([]); setAnswerState(null); }} disabled={resolvedAnswer || selectedLetterIndexes.length === 0}><Trash2 aria-hidden="true" /></IconButton>
            <IconButton label={mobileCopy.vocabularyBackspaceAction} onClick={() => { setSelectedLetterIndexes((current) => current.slice(0, -1)); setAnswerState(null); }} disabled={resolvedAnswer || selectedLetterIndexes.length === 0}><Delete aria-hidden="true" /></IconButton>
            <button type="button" className="primary-button" disabled={answerFeedbackVisible} onClick={dismissedCorrectFeedback ? next : () => checkTextAnswer(selectedAnswer)}>{dismissedCorrectFeedback ? copy.next : copy.check}</button>
          </div>
          <section className="vocabulary-hints" aria-label={mobileCopy.vocabularyMeaningHintLabel}>
            <h2>{mobileCopy.vocabularyMeaningHintLabel}</h2>
            {hintStage >= 1 ? <p dir="auto">{vocabularyTranslation(scenario, item.vocabularyId) || mobileCopy.translationError}</p> : null}
            <div className="exercise-actions">
              <button type="button" className="secondary-button" disabled={resolvedAnswer || hintStage >= 1} onClick={() => setHintStage(1)}><Languages aria-hidden="true" />{mobileCopy.vocabularyHintOneAction}</button>
              <button type="button" className="secondary-button" disabled={resolvedAnswer || hintStage < 1} onClick={() => void playUnscrambleHint()}><Volume2 aria-hidden="true" />{mobileCopy.vocabularyHintTwoAction}</button>
              {hintStage >= 2 ? <button type="button" className="secondary-button" disabled={resolvedAnswer} onClick={() => { stopWebAudio(); setAnswerState("give_up"); setAnswerFeedbackVisible(true); }}>{mobileCopy.vocabularyGiveUpAction}</button> : null}
            </div>
            {hintAudioError ? <p className="error-status" role="alert">{mobileCopy.audioError}</p> : null}
          </section>
        </div>
      ) : null}

      {activeMode === "listen_write" ? (
        <div className="exercise-panel listen-write-panel">
          <div className="answer-field">
            <div className="answer-field-heading">
              <label htmlFor="vocabulary-written-answer">{mobileCopy.vocabularyYourAnswerLabel}</label>
              {isAlphabetSpellingHintAvailable(import.meta.env.DEV) ? (
                <SpellingHint term={term} setup={setup} disabled={resolvedAnswer} />
              ) : null}
            </div>
            <input id="vocabulary-written-answer" value={writtenAnswer} disabled={resolvedAnswer} onChange={(event) => setWrittenAnswer(event.target.value)} autoComplete="off" dir="auto" />
          </div>
          <button type="button" className="primary-button" disabled={answerFeedbackVisible} onClick={dismissedCorrectFeedback ? next : () => checkTextAnswer(normalizeVocabularyAnswer(writtenAnswer, setup.targetLanguage))}>{dismissedCorrectFeedback ? copy.next : copy.check}</button>
        </div>
      ) : null}

      <AnswerFeedbackDialog
        state={answerState}
        visible={answerFeedbackVisible}
        closeLabel={copy.close}
        correctLabel={copy.correct}
        retryLabel={copy.tryAgain}
        nextLabel={copy.next}
        onClose={dismissAnswerFeedback}
        onNext={next}
        revealedAnswer={term}
        revealLabel={mobileCopy.vocabularyFeedbackGiveUp}
        giveUpLabel={mobileCopy.vocabularyGiveUpAction}
        onGiveUp={activeMode === "listen_write" && (failuresById[item.vocabularyId] || 0) >= 2 ? () => { stopWebAudio(); setAnswerState("give_up"); setAnswerFeedbackVisible(true); } : undefined}
      />
      <FeedbackDialog
        feedback={speech.feedback}
        closeLabel={copy.close}
        onClose={() => { if (repeatComplete) next(); else speech.clear(); }}
        onNext={speech.feedback?.band === "PASS" ? next : undefined}
        nextLabel={copy.next}
        transcriptLabel={copy.transcript}
      />
        </>
      )}
    </section>
  );
}

function buildWebMissionHintText(expectedUtterance: string, revealLevel: number): string {
  const text = String(expectedUtterance || "").trim();
  if (!text || revealLevel <= 0) return "";
  if (revealLevel >= 3) return text;
  const maskWord = (word: string, visibleCharacters: number) => {
    let visible = 0;
    return Array.from(word).map((character) => {
      if (!/[\p{L}\p{N}]/u.test(character)) return character;
      if (visible < visibleCharacters) {
        visible += 1;
        return character;
      }
      return "_";
    }).join("");
  };
  return text.split(/\s+/u).map((word, wordIndex) => (
    revealLevel === 1 || wordIndex > 0 ? maskWord(word, 1) : word
  )).join(" ");
}

function MissionCard(props: {
  mission: TaskSimulationObjective;
  scenario: ScenarioPackage;
  setup: LearnerSetup;
  compact?: boolean;
  onSuccess?: (feedback: SpeechAttemptFeedback) => void;
  onSkip?: () => void;
  audioAccess?: ReturnType<typeof useWebAudioAccess>;
}) {
  const targetSentence = getSentenceById(props.scenario, props.mission.sentenceId);
  const leadSentence = getSentenceById(props.scenario, props.mission.systemLeadSentenceId);
  const replySentence = getSentenceById(props.scenario, props.mission.systemReplySentenceId);
  const speech = useSpeechAttempt(props.setup, props.scenario);
  const copy = WEB_COPY[props.setup.supportLanguage];
  const mobileCopy = getCopy(props.setup.supportLanguage);
  const [phase, setPhase] = useState<"intro" | "preview" | "practice">("intro");
  const [startingPractice, setStartingPractice] = useState(false);
  const [missionTurnPhase, setMissionTurnPhase] = useState<"idle" | "lead" | "learner" | "reply" | "complete">("idle");
  const [successFeedbackReady, setSuccessFeedbackReady] = useState(false);
  const [hintRevealLevel, setHintRevealLevel] = useState(0);
  const [reviewedTurnIndices, setReviewedTurnIndices] = useState<number[]>([]);
  const practiceRun = useRef(0);
  const handledPassFeedback = useRef<SpeechAttemptFeedback | null>(null);
  const previewTurns = [
    leadSentence ? { sentence: leadSentence, speaker: "system" as const } : null,
    targetSentence ? { sentence: targetSentence, speaker: "learner" as const } : null,
    replySentence ? { sentence: replySentence, speaker: "system" as const } : null,
  ].filter((turn): turn is { sentence: SentenceEntry; speaker: "learner" | "system" } => Boolean(turn?.sentence.text));
  const requiredTurnIndices = previewTurns.flatMap((turn, index) => turn.sentence.audioRef ? [index] : []);
  const activeTurnIndex = requiredTurnIndices.find((index) => !reviewedTurnIndices.includes(index));
  const previewReady = activeTurnIndex === undefined;

  useEffect(() => () => {
    practiceRun.current += 1;
    stopWebAudio();
  }, []);

  useEffect(() => {
    const feedback = speech.feedback;
    if (
      phase !== "practice"
      || feedback?.band !== "PASS"
      || handledPassFeedback.current === feedback
    ) {
      return;
    }

    handledPassFeedback.current = feedback;
    const run = practiceRun.current;
    let cancelled = false;
    const shouldPlayReply = Boolean(replySentence?.text) && !props.mission.systemReplyDisabled;

    const completeDialogue = async () => {
      if (shouldPlayReply && replySentence) {
        setMissionTurnPhase("reply");
        if (replySentence.audioRef) {
          try {
            await playSentenceAudio({
              sentence: replySentence,
              audioAccess: props.audioAccess,
              remoteRequest: buildRemoteAudioRequest(props.scenario, replySentence, "scenarioMissions", "scenario_missions"),
            });
          } catch {
            // A failed follow-up must not strand an otherwise completed mission.
          }
        }
      }

      if (cancelled || run !== practiceRun.current) return;
      setMissionTurnPhase("complete");
      setSuccessFeedbackReady(true);
    };

    void completeDialogue();
    return () => {
      cancelled = true;
    };
  }, [
    phase,
    props.audioAccess,
    props.mission.systemReplyDisabled,
    props.scenario,
    replySentence,
    speech.feedback,
  ]);

  if (!targetSentence) {
    return null;
  }

  const renderTurn = (
    turn: { sentence: SentenceEntry; speaker: "learner" | "system" },
    index: number,
    locked = false,
  ) => (
    <div key={`${turn.sentence.sentenceId}_${turn.speaker}_${index}`} className={`dialogue-turn ${turn.speaker === "learner" ? "learner-turn" : "system-turn"}`} dir="auto">
      <AudioButton
        sentence={turn.sentence}
        label={mobileCopy.listen}
        audioAccess={props.audioAccess}
        remoteRequest={buildRemoteAudioRequest(props.scenario, turn.sentence, "scenarioMissions", "scenario_missions")}
        disabled={locked}
        onPlayed={phase === "preview" ? () => setReviewedTurnIndices((current) => current.includes(index) ? current : [...current, index]) : undefined}
      />
      <span>{turn.speaker === "learner" ? copy.yourTurn : "W-Echo"}</span>
      <p>{turn.sentence.text}</p>
      <TranslationDisclosure
        label={copy.translate}
        supportLanguage={props.setup.supportLanguage}
        targetLanguage={props.setup.targetLanguage}
        text={sentenceTranslation(props.scenario, turn.sentence.sentenceId)}
      />
    </div>
  );

  const startPractice = async () => {
    if (startingPractice) return;
    const run = ++practiceRun.current;
    setStartingPractice(true);
    setPhase("practice");
    setMissionTurnPhase("learner");
    setSuccessFeedbackReady(false);
    setHintRevealLevel(0);
    handledPassFeedback.current = null;
    speech.resetAttempts();
    const normalizedRoleType = String(props.mission.roleType || "").trim().toLowerCase();
    const shouldPlayLead = !normalizedRoleType.startsWith("initiate") && Boolean(leadSentence?.audioRef);
    if (shouldPlayLead && leadSentence) {
      setMissionTurnPhase("lead");
      try {
        await playSentenceAudio({
          sentence: leadSentence,
          audioAccess: props.audioAccess,
          remoteRequest: buildRemoteAudioRequest(props.scenario, leadSentence, "scenarioMissions", "scenario_missions"),
        });
      } catch {
        // A failed lead line must not leave the learner stranded before their turn.
      }
    }
    if (run !== practiceRun.current) return;
    setMissionTurnPhase("learner");
    setStartingPractice(false);
    await speech.run(targetSentence.text);
  };

  const passFeedback = speech.feedback?.band === "PASS" ? speech.feedback : null;
  const visibleFeedback = passFeedback && !successFeedbackReady ? null : speech.feedback;
  const replyVisible = Boolean(replySentence)
    && !props.mission.systemReplyDisabled
    && (missionTurnPhase === "reply" || missionTurnPhase === "complete");
  const dialogueCompleting = missionTurnPhase === "reply";
  const liveAudioLocked = startingPractice || speech.busy || dialogueCompleting;
  const completeMission = () => {
    if (!passFeedback || !successFeedbackReady || !props.onSuccess) return;
    props.onSuccess(passFeedback);
  };
  const retryMission = () => {
    speech.clear();
    setMissionTurnPhase("learner");
    void speech.run(targetSentence.text);
  };
  const skipMission = () => {
    practiceRun.current += 1;
    stopWebAudio();
    speech.clear();
    props.onSkip?.();
  };

  if (props.compact) {
    return (
      <div className="mission-card compact">
        {props.mission.missionGoal || props.mission.missionScene ? (
          <div className="mission-brief">
            {props.mission.missionScene ? <span>{props.mission.missionScene}</span> : null}
            {props.mission.missionGoal ? <strong>{props.mission.missionGoal}</strong> : null}
          </div>
        ) : null}
        {previewTurns.map((turn, index) => renderTurn(turn, index))}
      </div>
    );
  }

  return (
    <div className={`mission-card mission-${phase}`}>
      {phase === "intro" ? (
        <>
          {props.mission.missionScene ? <div className="mission-brief"><span>{mobileCopy.simulationSceneLabel}</span><strong>{props.mission.missionScene}</strong></div> : null}
          {props.mission.missionGoal ? <div className="mission-brief"><span>{mobileCopy.simulationGoalLabel}</span><strong>{props.mission.missionGoal}</strong></div> : null}
          <p className="mission-instruction">{mobileCopy.simulationPreviewListenRequired}</p>
          <button type="button" className="primary-button" onClick={() => setPhase("preview")}>{mobileCopy.simulationPreviewReadyButton}</button>
        </>
      ) : null}
      {phase === "preview" ? (
        <>
          <div className="mission-preview-status" role="status">
            <span>{mobileCopy.simulationPreviewProgressLabel}</span>
            <strong>{reviewedTurnIndices.filter((index) => requiredTurnIndices.includes(index)).length}/{requiredTurnIndices.length}</strong>
            <small>{previewReady ? mobileCopy.simulationPreviewReadyLabel : mobileCopy.simulationPreviewListenRequired}</small>
          </div>
          {previewTurns.map((turn, index) => renderTurn(turn, index, !previewReady && index !== activeTurnIndex))}
          <button type="button" className="primary-button" disabled={!previewReady || startingPractice} onClick={() => void startPractice()}>{mobileCopy.simulationStartButton}</button>
        </>
      ) : null}
      {phase === "practice" ? (
        <>
          {leadSentence ? renderTurn({ sentence: leadSentence, speaker: "system" }, 0, liveAudioLocked) : null}
          <div className="mission-live-prompt" dir="auto">
            <span>{copy.yourTurn}</span>
            {hintRevealLevel > 0 ? (
              <p className="mission-hint-text"><strong>{mobileCopy.simulationHintTitle}</strong>{buildWebMissionHintText(targetSentence.text, hintRevealLevel)}</p>
            ) : null}
            {hintRevealLevel < 3 && !passFeedback ? (
              <button type="button" className="secondary-button mission-hint-action" onClick={() => setHintRevealLevel((current) => Math.min(3, current + 1))}>
                {mobileCopy.simulationShowHint}
              </button>
            ) : null}
          </div>
          {replyVisible && replySentence ? renderTurn({ sentence: replySentence, speaker: "system" }, 2, liveAudioLocked) : null}
          {startingPractice || dialogueCompleting ? <p className="mission-live-status" role="status">{mobileCopy.audioPlaying}</p> : null}
          {!passFeedback ? (
            <SpeechButton
              busy={speech.busy}
              disabled={startingPractice || missionTurnPhase !== "learner"}
              label={speech.busy ? mobileCopy.practiceListening : mobileCopy.practiceTryVoice}
              microphoneMissingLabel={copy.browserMicrophoneMissing}
              unavailableLabel={copy.browserSpeechUnavailable}
              onStart={() => void speech.run(targetSentence.text)}
            />
          ) : null}
          {speech.error ? <p className="error-status" role="alert">{describeSpeechError(speech.error, props.setup.supportLanguage)}</p> : null}
        </>
      ) : null}
      <FeedbackDialog
        feedback={visibleFeedback}
        closeLabel={copy.close}
        onClose={passFeedback && successFeedbackReady ? completeMission : speech.clear}
        onNext={passFeedback && successFeedbackReady && props.onSuccess ? completeMission : undefined}
        onRetry={visibleFeedback && visibleFeedback.band !== "PASS" ? retryMission : undefined}
        onSkip={visibleFeedback && visibleFeedback.band !== "PASS" && props.onSkip ? skipMission : undefined}
        nextLabel={copy.next}
        retryLabel={mobileCopy.simulationRetryButton}
        skipLabel={mobileCopy.assistedProgressAction}
        actionHint={visibleFeedback && visibleFeedback.band !== "PASS" && props.onSkip ? mobileCopy.assistedProgressHint : undefined}
        transcriptLabel={copy.transcript}
      />
    </div>
  );
}

function MissionsView({
  scenario,
  setup,
  audioAccess,
  onTutorialComplete,
  progress,
  onNaturalBreak,
  onOpenHome,
}: ViewProps & { onTutorialComplete?: () => void; progress?: WebProgressController; onOpenHome?: () => void }) {
  const missions = useMemo(() => (
    onTutorialComplete
      ? getTutorialMissions(scenario)
      : (scenario.taskStructure.simulationObjectives || [])
  ), [onTutorialComplete, scenario]);
  const [index, setIndex] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  const copy = WEB_COPY[setup.supportLanguage];
  const mission = missions[index] || null;
  useEffect(() => { setIndex(0); setSessionComplete(false); }, [scenario.scenarioId]);
  const advanceMission = () => {
    if (index >= missions.length - 1) setSessionComplete(true);
    else setIndex((current) => current + 1);
  };
  const completeMission = () => {
    if (!mission) return;
    if (progress) {
      progress.update((current) => recordMissionResult(
        current,
        scenario.scenarioId,
        mission.objectiveId,
        true,
        index,
      ));
    }
    if (onTutorialComplete) {
      onTutorialComplete();
    } else {
      onNaturalBreak?.("missionResult");
      advanceMission();
    }
  };
  const skipMission = () => {
    if (onTutorialComplete) {
      onTutorialComplete();
    } else {
      advanceMission();
    }
  };
  return (
    <section className="view lesson-view" aria-labelledby="missions-title">
      <div className="lesson-topline">
        <div><span>{copy.missions}</span><h1 id="missions-title">{sessionComplete ? scenario.title : mission?.missionScene || scenario.title}</h1></div>
        <strong>{missions.length ? `${index + 1}/${missions.length}` : "0/0"}</strong>
      </div>
      {sessionComplete ? (
        <section className="lesson-completion" role="status" aria-live="polite">
          <span className="completion-mark"><Check aria-hidden="true" /></span>
          <h2>{copy.missionsComplete}</h2>
          <div className="completion-actions">
            <button type="button" className="primary-button" onClick={() => { setIndex(0); setSessionComplete(false); }}><RotateCcw aria-hidden="true" />{copy.missionsReplay}</button>
            {onOpenHome ? <button type="button" className="secondary-button" onClick={onOpenHome}><ArrowLeft aria-hidden="true" />{copy.home}</button> : null}
          </div>
        </section>
      ) : mission ? (
        <MissionCard
          key={mission.objectiveId}
          mission={mission}
          scenario={scenario}
          setup={setup}
          audioAccess={audioAccess}
          onSuccess={completeMission}
          onSkip={skipMission}
        />
      ) : null}
      {missions.length > 1 && !onTutorialComplete && !sessionComplete ? (
        <div className="pager-actions">
          <IconButton label={getCopy(setup.supportLanguage).previous} disabled={index === 0} onClick={() => setIndex((current) => Math.max(0, current - 1))}><ArrowLeft aria-hidden="true" /></IconButton>
          <IconButton label={copy.next} onClick={advanceMission}><ArrowRight aria-hidden="true" /></IconButton>
        </div>
      ) : null}
    </section>
  );
}

function MixedMissionsView({
  scenario,
  setup,
  audioAccess,
  onTutorialComplete,
  progress,
  installedScenarios,
  onNaturalBreak,
  progressByLevel,
  loading = false,
}: Omit<ViewProps, "scenario"> & {
  scenario: ScenarioPackage | null;
  onTutorialComplete?: () => void;
  progress?: WebProgressController;
  progressByLevel?: Record<LearningLevel, WebProgressController>;
  installedScenarios?: ResolvedWebScenario[];
  loading?: boolean;
}) {
  type MixedMissionItem = {
    level: LearningLevel;
    mission: TaskSimulationObjective;
    scenario: ScenarioPackage;
  };
  const allMissionItems = useMemo<MixedMissionItem[]>(() => {
    if (onTutorialComplete) {
      return scenario
        ? getTutorialMissions(scenario).map((mission) => ({ mission, scenario, level: "a1" as LearningLevel }))
        : [];
    }
    const sources = installedScenarios?.length
      ? installedScenarios.map((item) => item.scenario)
      : scenario
        ? [scenario]
        : [];
    return sources.flatMap((sourceScenario) => (
      (sourceScenario.taskStructure.simulationObjectives || []).map((mission) => ({
        mission,
        scenario: sourceScenario,
        level: (installedScenarios?.find((candidate) => candidate.scenario.scenarioId === sourceScenario.scenarioId)?.entry.level || "a1") as LearningLevel,
      }))
    ));
  }, [installedScenarios, onTutorialComplete, scenario]);
  const [missionItems, setMissionItems] = useState<MixedMissionItem[]>([]);
  const [audioReadinessLoading, setAudioReadinessLoading] = useState(false);
  const isUnitReady = audioAccess?.isUnitReady;
  useEffect(() => {
    let active = true;
    if (onTutorialComplete || !isUnitReady) {
      setMissionItems(allMissionItems);
      setAudioReadinessLoading(false);
      return () => { active = false; };
    }
    setAudioReadinessLoading(true);
    const scenarioItems = Array.from(new Map(
      allMissionItems.map((item) => [item.scenario.scenarioId, item]),
    ).values());
    void Promise.all(scenarioItems.map(async (item) => {
      const request = buildScenarioRouteAudioRequest(item.scenario, "missions");
      return request && await isUnitReady(request) ? item.scenario.scenarioId : null;
    })).then((scenarioIds) => {
      if (!active) return;
      const readyScenarioIds = new Set(scenarioIds.filter((value): value is string => Boolean(value)));
      setMissionItems(allMissionItems.filter((item) => readyScenarioIds.has(item.scenario.scenarioId)));
      setAudioReadinessLoading(false);
    }).catch(() => {
      if (!active) return;
      setMissionItems([]);
      setAudioReadinessLoading(false);
    });
    return () => { active = false; };
  }, [allMissionItems, isUnitReady, onTutorialComplete]);
  const [missionQueue, setMissionQueue] = useState<MixedMissionItem[]>([]);
  const [missionQueueIndex, setMissionQueueIndex] = useState(0);
  const [completedQueueLength, setCompletedQueueLength] = useState<number | null>(null);
  const selected = missionQueue[missionQueueIndex] || null;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [levelFilter, setLevelFilter] = useState<"all" | LearningLevel>("all");
  const copy = WEB_COPY[setup.supportLanguage];
  const mobileCopy = getCopy(setup.supportLanguage);
  const levelFilters = ["all", "a1", "a2", "b1", "b2"] as const;
  const filteredMissionItems = missionItems.filter((item) => {
    if (levelFilter === "all") {
      return true;
    }
    return item.level === levelFilter;
  });
  const scenarioCount = new Set(missionItems.map((item) => item.scenario.scenarioId)).size;
  const levelCount = (level: LearningLevel) => missionItems.filter((item) => item.level === level).length;
  const startMixedMissionQueue = () => {
    if (!filteredMissionItems.length) {
      return;
    }
    setMissionQueue(shuffleMissionQueue(filteredMissionItems));
    setCompletedQueueLength(null);
    setMissionQueueIndex(0);
  };
  const openMission = (item: MixedMissionItem) => {
    setMissionQueue([item]);
    setCompletedQueueLength(null);
    setMissionQueueIndex(0);
  };
  const closeMissionQueue = () => {
    setMissionQueue([]);
    setMissionQueueIndex(0);
    setCompletedQueueLength(null);
  };
  const advanceMissionQueue = () => {
    const nextIndex = nextMissionQueueIndex(missionQueueIndex, missionQueue.length);
    if (nextIndex === null) {
      closeMissionQueue();
      setCompletedQueueLength(missionQueue.length);
      return;
    }
    setMissionQueueIndex(nextIndex);
  };
  return (
    <section className="view mixed-view" aria-labelledby="mixed-title">
      <div className="view-heading">
        <span>MIX</span>
        <h1 id="mixed-title">{copy.mixed}</h1>
      </div>
      {selected ? (
        <>
          <div className="mixed-session-toolbar">
            <button type="button" className="text-button" onClick={closeMissionQueue}><ArrowLeft aria-hidden="true" />{copy.mixed}</button>
            <strong>{missionQueueIndex + 1}/{missionQueue.length}</strong>
          </div>
          {onTutorialComplete ? (
            <div className="tutorial-mission-selection">
              <MissionCard mission={selected.mission} scenario={selected.scenario} setup={setup} compact />
              <button type="button" className="primary-button" onClick={onTutorialComplete}>
                {getCopy(setup.supportLanguage).mixedMissionsOpenAction}
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          ) : (
            <MissionCard
              key={`${selected.scenario.scenarioId}:${selected.mission.objectiveId}`}
              mission={selected.mission}
              scenario={selected.scenario}
              setup={setup}
              audioAccess={audioAccess}
              onSuccess={() => {
                const missionProgress = progressByLevel?.[selected.level] || progress;
                if (missionProgress) {
                  missionProgress.update((current) => recordMissionResult(
                    current,
                    selected.scenario.scenarioId,
                    selected.mission.objectiveId,
                    true,
                    missionItems.findIndex((item) => (
                      item.scenario.scenarioId === selected.scenario.scenarioId
                      && item.mission.objectiveId === selected.mission.objectiveId
                    )),
                  ));
                }
                onNaturalBreak?.("missionResult");
                advanceMissionQueue();
              }}
              onSkip={advanceMissionQueue}
            />
          )}
        </>
      ) : completedQueueLength !== null ? (
        <section className="lesson-completion" role="status" aria-live="polite">
          <span className="completion-mark"><Check aria-hidden="true" /></span>
          <h2>{copy.missionsComplete}</h2>
          <strong>{completedQueueLength} {mobileCopy.modeStatsMissions}</strong>
          <div className="completion-actions">
            <button type="button" className="primary-button" onClick={startMixedMissionQueue}><RotateCcw aria-hidden="true" />{copy.missionsReplay}</button>
            <button type="button" className="secondary-button" onClick={() => setCompletedQueueLength(null)}><ArrowLeft aria-hidden="true" />{mobileCopy.mixedMissionsPickerTitle}</button>
          </div>
        </section>
      ) : loading || audioReadinessLoading ? (
        <div className="library-state mixed-loading-state" aria-live="polite">
          <span className="loading-ring" aria-hidden="true" />
          <strong>{mobileCopy.mixedMissionsLoadingTitle}</strong>
          <p>{mobileCopy.mixedMissionsLoadingHint}</p>
        </div>
      ) : missionItems.length ? (
        <>
          <div className="mixed-summary">
            <div><strong>{missionItems.length}</strong><span>{mobileCopy.modeStatsMissions}</span></div>
            <div><strong>{scenarioCount}</strong><span>{mobileCopy.scenarios}</span></div>
            <div><strong>{levelFilter === "all" ? mobileCopy.mixedMissionsAllLevelsFilter : levelFilter.toUpperCase()}</strong><span>{mobileCopy.mixedMissionsLevelFilterLabel}</span></div>
          </div>
          <div className="mixed-filter-row" role="group" aria-label={mobileCopy.mixedMissionsLevelFilterLabel}>
            {levelFilters.map((level) => {
              const count = level === "all" ? missionItems.length : levelCount(level);
              return (
                <button
                  key={level}
                  type="button"
                  className={levelFilter === level ? "is-active" : ""}
                  disabled={count === 0}
                  onClick={() => setLevelFilter(level)}
                >
                  <span>{level === "all" ? mobileCopy.mixedMissionsAllLevelsFilter : level.toUpperCase()}</span>
                  <strong>{count}</strong>
                </button>
              );
            })}
          </div>
          {!onTutorialComplete ? (
            <button type="button" className="primary-button mixed-random-action" onClick={startMixedMissionQueue}>
              <Shuffle aria-hidden="true" />
              <span>{mobileCopy.mixedMissionsStartMixedAction}</span>
              <strong>{filteredMissionItems.length}</strong>
            </button>
          ) : null}
          <button type="button" className="mixed-picker-action" onClick={() => setPickerOpen(true)}>
            <span><strong>{mobileCopy.mixedMissionsPickMissionAction}</strong><small>{levelFilter === "all" ? mobileCopy.mixedMissionsAllLevelsFilter : levelFilter.toUpperCase()} · {filteredMissionItems.length} {mobileCopy.modeStatsMissions}</small></span>
            <ArrowRight aria-hidden="true" />
          </button>
          {pickerOpen ? (
            <div className="dialog-backdrop" role="presentation" onMouseDown={() => setPickerOpen(false)}>
              <section className="mixed-picker-dialog" role="dialog" aria-modal="true" aria-labelledby="mixed-picker-title" onMouseDown={(event) => event.stopPropagation()}>
                <header>
                  <div><h2 id="mixed-picker-title">{mobileCopy.mixedMissionsPickerTitle}</h2><span>{filteredMissionItems.length} {mobileCopy.modeStatsMissions}</span></div>
                  <IconButton label={mobileCopy.mixedMissionsPickerCloseAction} onClick={() => setPickerOpen(false)}><X aria-hidden="true" /></IconButton>
                </header>
                <div className="mission-list mixed-picker-list">
                  {filteredMissionItems.map((item, index) => {
                    const sentence = getSentenceById(item.scenario, item.mission.sentenceId);
                    return (
                      <button
                        key={`${item.scenario.scenarioId}:${item.mission.objectiveId}`}
                        type="button"
                        onClick={() => { setPickerOpen(false); openMission(item); }}
                      >
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <strong>{item.mission.missionGoal || sentence?.text || `${mobileCopy.simulationMissionLabel} ${index + 1}`}</strong>
                        <ArrowRight aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          ) : null}
        </>
      ) : (
        <div className="empty-state mixed-empty-state">
          <strong>{mobileCopy.mixedMissionsEmptyTitle}</strong>
          <p>{mobileCopy.mixedMissionsEmptyHint}</p>
        </div>
      )}
    </section>
  );
}

function resolveGuidedTrialProgress(phase: GuidedTrialPhase): { current: number; vocabularyStep: number | null } {
  if (phase === "practice") {
    return { current: 1, vocabularyStep: null };
  }
  if (phase.startsWith("vocabulary_")) {
    const vocabularyStep = phase === "vocabulary_unscramble"
      ? 2
      : phase === "vocabulary_listen_write"
        ? 3
        : 1;
    return { current: 2, vocabularyStep };
  }
  if (phase === "missions") {
    return { current: 3, vocabularyStep: null };
  }
  return { current: 4, vocabularyStep: null };
}

function resolveGuidedTrialCopy(phase: GuidedTrialPhase, supportLanguage: SupportLanguage) {
  const copy = getCopy(supportLanguage);
  if (phase.startsWith("vocabulary_")) {
    const step = resolveGuidedTrialProgress(phase).vocabularyStep || 1;
    return copy.controlledTrialVocabularyStages[step - 1];
  }
  const progress = resolveGuidedTrialProgress(phase);
  return copy.controlledTrialStages[progress.current - 1];
}

function GuidedTrialHeader(props: { phase: GuidedTrialPhase; setup: LearnerSetup }) {
  const copy = getCopy(props.setup.supportLanguage);
  const progress = resolveGuidedTrialProgress(props.phase);
  return (
    <header className="guided-trial-header">
      <div>
        <span>{copy.controlledTrialProgressLabel}</span>
        <strong>{progress.current}/4</strong>
        {progress.vocabularyStep ? <small>{progress.vocabularyStep}/3</small> : null}
      </div>
      <div className="guided-trial-progress" aria-label={`${progress.current}/4`}>
        {[1, 2, 3, 4].map((step) => (
          <span key={step} className={step <= progress.current ? "is-complete" : ""} />
        ))}
      </div>
    </header>
  );
}

function GuidedTrialGateway(props: {
  phase: GuidedTrialPhase;
  setup: LearnerSetup;
  onStart: () => void;
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
}) {
  const mobileCopy = getCopy(props.setup.supportLanguage);
  const stageCopy = resolveGuidedTrialCopy(props.phase, props.setup.supportLanguage);
  const progress = resolveGuidedTrialProgress(props.phase);
  return (
    <main className={`guided-trial-gateway ${premiumExperienceClassName}`.trim()}>
      <AppearanceOptions
        appearance={props.appearance}
        className="standalone-theme-options"
        label={WEB_COPY[props.setup.supportLanguage].theme}
        onAppearance={props.onAppearance}
      />
      <GuidedTrialHeader phase={props.phase} setup={props.setup} />
      <section>
        <span>{String(progress.current).padStart(2, "0")}</span>
        <h1>{stageCopy.title}</h1>
        <p>{stageCopy.body}</p>
      </section>
      <button type="button" className="primary-button" onClick={props.onStart}>
        {mobileCopy.controlledTrialStart}
      </button>
    </main>
  );
}

function GuidedTrialCompletion(props: {
  setup: LearnerSetup;
  onContinue: () => void;
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
}) {
  const copy = getCopy(props.setup.supportLanguage);
  return (
    <main className={`guided-trial-completion ${premiumExperienceClassName}`.trim()}>
      <AppearanceOptions
        appearance={props.appearance}
        className="standalone-theme-options"
        label={WEB_COPY[props.setup.supportLanguage].theme}
        onAppearance={props.onAppearance}
      />
      <span className="completion-mark" aria-hidden="true"><Check /></span>
      <div>
        <span>{copy.controlledTrialProgressLabel} · 4/4</span>
        <h1>{copy.controlledTrialCompletionTitle}</h1>
        <p>{copy.controlledTrialCompletionBody}</p>
      </div>
      <ul>
        {copy.controlledTrialCompletionItems.map((item) => (
          <li key={item}><Check aria-hidden="true" /><span>{item}</span></li>
        ))}
      </ul>
      <button type="button" className="primary-button" onClick={props.onContinue}>
        {copy.controlledTrialCompletionAction}
        <ArrowRight aria-hidden="true" />
      </button>
    </main>
  );
}

function AccountGateway(props: {
  setup: LearnerSetup;
  auth: ReturnType<typeof useWebAuth>;
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
}) {
  const copy = WEB_COPY[props.setup.supportLanguage];
  return (
    <main className={`account-gateway ${premiumExperienceClassName}`.trim()}>
      <AppearanceOptions
        appearance={props.appearance}
        className="standalone-theme-options"
        label={copy.theme}
        onAppearance={props.onAppearance}
      />
      <img src={logoUrl} width="72" height="72" alt="" />
      <div>
        <span lang="en">Wordinarius Echo</span>
        <h1>{copy.accountGatewayTitle}</h1>
        <p>{copy.accountGatewayBody}</p>
      </div>
      <div className="account-actions">
        <button type="button" className="primary-button" onClick={() => void props.auth.signInWithGoogle()}>
          <LogIn aria-hidden="true" />{copy.googleSignIn}
        </button>
        <button type="button" className="secondary-button" onClick={() => void props.auth.signInWithApple()}>
          <LogIn aria-hidden="true" />{copy.appleSignIn}
        </button>
      </div>
      {props.auth.error ? (
        <p className="error-status">{describeWebAuthError(props.auth.error, props.setup.supportLanguage)}</p>
      ) : null}
    </main>
  );
}

function GuidedTrialStage(props: {
  phase: GuidedTrialPhase;
  scenario: ScenarioPackage;
  setup: LearnerSetup;
  onComplete: () => void;
}) {
  const vocabularyMode: VocabularyMode | undefined = props.phase === "vocabulary_listen_repeat"
    ? "listen_repeat"
    : props.phase === "vocabulary_unscramble"
      ? "unscramble"
      : props.phase === "vocabulary_listen_write"
        ? "listen_write"
        : undefined;
  return (
    <div className={`guided-trial-stage ${premiumExperienceClassName}`.trim()}>
      <GuidedTrialHeader phase={props.phase} setup={props.setup} />
      <div className="guided-trial-content">
        {props.phase === "practice" ? (
          <PracticeView scenario={props.scenario} setup={props.setup} onTutorialComplete={props.onComplete} />
        ) : null}
        {vocabularyMode ? (
          <VocabularyView
            scenario={props.scenario}
            setup={props.setup}
            forcedMode={vocabularyMode}
            onTutorialComplete={props.onComplete}
          />
        ) : null}
        {props.phase === "missions" ? (
          <MissionsView scenario={props.scenario} setup={props.setup} onTutorialComplete={props.onComplete} />
        ) : null}
        {props.phase === "mixed_missions" ? (
          <MixedMissionsView scenario={props.scenario} setup={props.setup} onTutorialComplete={props.onComplete} />
        ) : null}
      </div>
    </div>
  );
}

function ProfileView(props: {
  setup: LearnerSetup;
  appearance: Appearance;
  onAppearance: (appearance: Appearance) => void;
  onResetSetup: () => void;
  onChangeLanguages: () => void;
  auth: ReturnType<typeof useWebAuth>;
  monetization: ReturnType<typeof useWebMonetization>;
  reminders: ReturnType<typeof useWebReminders>;
  onRestartGuides: () => void;
}) {
  const copy = WEB_COPY[props.setup.supportLanguage];
  const [activePanel, setActivePanel] = useState<"settings" | "access" | "account">("settings");
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState(false);
  const deleteAccount = async () => {
    if (!window.confirm(copy.accountDeleteConfirm)) {
      return;
    }
    setAccountBusy(true);
    setAccountError(false);
    try {
      await callAuthenticatedWordinariusFunction("deleteLearnerAccount", { confirmationText: "DELETE" });
      await props.auth.signOut();
      props.onResetSetup();
    } catch {
      setAccountError(true);
    } finally {
      setAccountBusy(false);
    }
  };
  return (
    <section className="view profile-view" aria-labelledby="profile-title">
      <div className="view-heading"><span className="brand-kicker" lang="en">WORDINARIUS ECHO</span><h1 id="profile-title">{copy.profile}</h1></div>
      <div className="profile-overview">
        <CircleUserRound aria-hidden="true" />
        <div>
          <strong>{props.auth.user?.displayName || props.auth.user?.email || copy.accountSignedOut}</strong>
          <span>{props.monetization.profile.subscriptionTier === "premium" ? copy.accountPremium : copy.accountFree}</span>
        </div>
      </div>
      <div className="profile-tabs" role="tablist" aria-label={copy.profile}>
        {([
          ["settings", copy.profileSettings],
          ["access", copy.profileAccess],
          ["account", copy.profileAccount],
        ] as const).map(([panel, label]) => (
          <button
            key={panel}
            type="button"
            role="tab"
            aria-selected={activePanel === panel}
            className={activePanel === panel ? "is-active" : ""}
            onClick={() => setActivePanel(panel)}
          >
            {label}
          </button>
        ))}
      </div>

      {activePanel === "settings" ? (
        <div className="profile-panel" role="tabpanel">
          <div className="settings-band appearance-settings">
            <div><strong>{copy.theme}</strong><span>{APPEARANCE_OPTIONS.find(({ value }) => value === props.appearance)?.label}</span></div>
            <AppearanceOptions appearance={props.appearance} label={copy.theme} onAppearance={props.onAppearance} />
          </div>
          <button type="button" className="settings-band profile-language-row" onClick={props.onChangeLanguages}>
            <span className="profile-row-label">{copy.profileLanguage}</span>
            <span className="language-summary">
              <LanguageFlag language={props.setup.supportLanguage} />
              <strong>{LANGUAGE_META[props.setup.supportLanguage].nativeName}</strong>
              <ArrowRight aria-hidden="true" />
              <LanguageFlag language={props.setup.targetLanguage} />
              <strong>{LANGUAGE_META[props.setup.targetLanguage].nativeName}</strong>
            </span>
          </button>
          <div className="settings-band reminder-settings">
            <div>
              <strong>{copy.reminders}</strong>
              <span>{props.reminders.settings.enabled ? copy.remindersOn : copy.remindersOff}</span>
            </div>
            {props.reminders.settings.enabled ? (
              <div className="reminder-controls">
                <select
                  aria-label={copy.reminderFrequency}
                  value={props.reminders.settings.frequency}
                  onChange={(event) => props.reminders.update({ frequency: event.target.value === "weekly" ? "weekly" : "daily" })}
                >
                  <option value="daily">{copy.reminderDaily}</option>
                  <option value="weekly">{copy.reminderWeekly}</option>
                </select>
                <input
                  type="time"
                  aria-label={copy.reminderTime}
                  value={props.reminders.settings.time}
                  onChange={(event) => props.reminders.update({ time: event.target.value })}
                />
                <button type="button" className="secondary-button" onClick={props.reminders.disable}>
                  <BellOff aria-hidden="true" />{copy.remindersDisable}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="secondary-button"
                onClick={() => void props.reminders.enable()}
                disabled={!props.reminders.supported || props.reminders.permission === "denied"}
              >
                <Bell aria-hidden="true" />{copy.remindersEnable}
              </button>
            )}
          </div>
          <div className="profile-action-list">
            <button type="button" onClick={showWebPrivacyOptions}>
              <Cookie aria-hidden="true" /><span>{copy.privacySettings}</span><ArrowRight aria-hidden="true" />
            </button>
            <button type="button" onClick={props.onRestartGuides}>
              <BookOpenText aria-hidden="true" /><span>{getCopy(props.setup.supportLanguage).firstUseGuideTitle}</span><ArrowRight aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}

      {activePanel === "access" ? (
        <div className="profile-panel account-section" role="tabpanel">
          <div className="profile-status-row">
            <div>
              <strong>{props.monetization.profile.subscriptionTier === "premium" ? copy.accountPremium : copy.accountFree}</strong>
              <span>{props.auth.user?.email || copy.accountSignedOut}</span>
            </div>
            {props.auth.user ? (
              <button type="button" className="secondary-button" onClick={() => void props.monetization.refresh()} disabled={props.monetization.loading}>
                <RefreshCw aria-hidden="true" />{copy.premiumRefresh}
              </button>
            ) : null}
          </div>
          {!props.auth.user ? (
            <div className="account-actions">
              <button type="button" className="primary-button" onClick={() => void props.auth.signInWithGoogle()}>
                <LogIn aria-hidden="true" />{copy.googleSignIn}
              </button>
              <button type="button" className="secondary-button" onClick={() => void props.auth.signInWithApple()}>
                <LogIn aria-hidden="true" />{copy.appleSignIn}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {activePanel === "account" ? (
        <div className="profile-panel account-section" role="tabpanel">
          <div className="account-summary">
            <CircleUserRound aria-hidden="true" />
            <div>
              <strong>{props.auth.user?.displayName || props.auth.user?.email || copy.accountSignedOut}</strong>
              <span>{props.monetization.profile.subscriptionTier === "premium" ? copy.accountPremium : copy.accountFree}</span>
            </div>
          </div>
          {!props.auth.user ? (
            <div className="account-actions">
              <button type="button" className="primary-button" onClick={() => void props.auth.signInWithGoogle()}>
                <LogIn aria-hidden="true" />{copy.googleSignIn}
              </button>
              <button type="button" className="secondary-button" onClick={() => void props.auth.signInWithApple()}>
                <LogIn aria-hidden="true" />{copy.appleSignIn}
              </button>
            </div>
          ) : (
            <div className="account-actions">
              <button type="button" className="secondary-button" onClick={() => void props.auth.signOut()}>
                <LogOut aria-hidden="true" />{copy.signOut}
              </button>
              <button type="button" className="danger-button" onClick={() => void deleteAccount()} disabled={accountBusy}>
                <Trash2 aria-hidden="true" />{copy.accountDelete}
              </button>
            </div>
          )}
          <button type="button" className="secondary-button" onClick={props.onChangeLanguages}>{copy.resetSetup}</button>
        </div>
      ) : null}

      <div className="profile-status-messages">
        {props.auth.error ? (
          <p className="error-status">{describeWebAuthError(props.auth.error, props.setup.supportLanguage)}</p>
        ) : props.monetization.error || accountError ? (
          <p className="error-status">{getCopy(props.setup.supportLanguage).genericError}</p>
        ) : null}
      </div>
    </section>
  );
}

export function App() {
  const [setup, setSetup] = useLearnerSetup();
  const [editingSetup, setEditingSetup] = useState(false);
  const [appearance, setAppearance] = useAppearance();
  const [route, setRoute] = useAppRoute();
  const auth = useWebAuth();
  const e2eSignedIn = import.meta.env.DEV
    && typeof window !== "undefined"
    && window.localStorage.getItem("wordinarius_echo.web.e2e_signed_in.v1") === "true";
  const signedIn = Boolean(auth.user) || e2eSignedIn;
  const guidedTrial = useGuidedTrial({
    accountId: auth.user?.uid || (e2eSignedIn ? "e2e-browser-user" : null),
    enabled: Boolean(setup && signedIn),
    syncRemote: Boolean(auth.user),
  });
  const monetization = useWebMonetization(auth.user);
  const adConsent = useWebAdConsent();
  const audioAccess = useWebAudioAccess({
    consent: adConsent,
    monetization: monetization.profile,
    onOpenProfile: () => setRoute("profile"),
    supportLanguage: setup?.supportLanguage || "en",
    user: auth.user,
  });
  const interstitials = useWebInterstitials({
    consent: adConsent,
    lastRewardedUnlockAtMs: audioAccess.lastRewardedUnlockAtMs,
    monetization: monetization.profile,
  });
  const reminderCopy = WEB_COPY[setup?.supportLanguage || "en"];
  const reminders = useWebReminders({
    title: reminderCopy.reminderNotificationTitle,
    body: reminderCopy.reminderNotificationBody,
  });
  const contextGuides = useWebContextGuides();
  const library = useWebContentLibrary(
    setup,
    Boolean(setup && guidedTrial.ready && guidedTrial.state.status === "completed" && signedIn),
  );
  const progressA1 = useWebProgress(setup && signedIn ? { targetLanguage: setup.targetLanguage, level: "a1" } : null, auth.user);
  const progressA2 = useWebProgress(setup && signedIn ? { targetLanguage: setup.targetLanguage, level: "a2" } : null, auth.user);
  const progressB1 = useWebProgress(setup && signedIn ? { targetLanguage: setup.targetLanguage, level: "b1" } : null, auth.user);
  const progressB2 = useWebProgress(setup && signedIn ? { targetLanguage: setup.targetLanguage, level: "b2" } : null, auth.user);
  const progressByLevel: Record<LearningLevel, WebProgressController> = {
    a1: progressA1,
    a2: progressA2,
    b1: progressB1,
    b2: progressB2,
  };
  const activeProgressLevel = library.activeScenario?.entry.level || library.selection.level || "a1";
  const progress = progressByLevel[activeProgressLevel];
  const activeScenario = library.activeScenario?.scenario;
  const routeAudioRequest = useMemo(() => activeScenario
    ? buildScenarioRouteAudioRequest(activeScenario, route)
    : null, [activeScenario, route]);
  const [tutorialScenario, setTutorialScenario] = useState<ScenarioPackage | null>(null);
  const [contentError, setContentError] = useState(false);
  const [requestedMode, setRequestedMode] = useState<AppRoute | null>(null);

  useEffect(() => () => stopWebAudio(), []);
  useEffect(() => {
    let active = true;
    setTutorialScenario(null);
    setContentError(false);
    if (!setup || !signedIn || !guidedTrial.ready || guidedTrial.state.status !== "active") {
      return () => { active = false; };
    }
    void loadTutorialScenario(setup.targetLanguage, setup.supportLanguage)
      .then((nextScenario) => {
        if (active) {
          setTutorialScenario(nextScenario);
        }
      })
      .catch(() => {
        if (active) {
          setContentError(true);
        }
      });
    return () => { active = false; };
  }, [guidedTrial.ready, guidedTrial.state.status, setup, signedIn]);
  useEffect(() => {
    if (
      setup
      && guidedTrial.state.status === "completed"
      && library.status === "ready"
      && !library.activeScenario
      && route !== "home"
      && route !== "profile"
      && route !== "mixed"
    ) {
      setRequestedMode(route);
      setRoute("home");
    }
  }, [guidedTrial.state.status, library.activeScenario, library.status, route, setup]);

  if (!setup) {
    return (
      <div className="web-app">
        <SetupFlow
          appearance={appearance}
          onAppearance={setAppearance}
          onComplete={(value) => { setSetup(value); setEditingSetup(false); setRoute("home"); }}
        />
      </div>
    );
  }

  const direction = directionForSupport(setup.supportLanguage);

  if (!e2eSignedIn && auth.status === "loading") {
    return (
      <div
        className={`web-app content-loading-screen ${premiumExperienceClassName}`.trim()}
        dir={direction}
        lang={setup.supportLanguage}
      >
        <img src={logoUrl} width="64" height="64" alt="" />
        <span className="loading-ring" aria-hidden="true" />
        <strong>{WEB_COPY[setup.supportLanguage].loadingContent}</strong>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div
        className="web-app"
        dir={direction}
        lang={setup.supportLanguage}
      >
        <AccountGateway
          setup={setup}
          auth={auth}
          appearance={appearance}
          onAppearance={setAppearance}
        />
        <StandaloneQuickAccess
          appearance={appearance}
          onAppearance={setAppearance}
          onLanguageSettings={() => setSetup(null)}
          profileActive
          setup={setup}
          supportLanguage={setup.supportLanguage}
        />
      </div>
    );
  }

  if (!guidedTrial.ready || (guidedTrial.state.status === "active" && !tutorialScenario)) {
    const copy = WEB_COPY[setup.supportLanguage];
    return (
      <div
        className={`web-app content-loading-screen ${premiumExperienceClassName}`.trim()}
        dir={direction}
        lang={setup.supportLanguage}
      >
        <img src={logoUrl} width="64" height="64" alt="" />
        {contentError ? (
          <>
            <strong>{copy.contentLoadFailed}</strong>
            <button type="button" className="secondary-button" onClick={() => setSetup(null)}>{copy.resetSetup}</button>
          </>
        ) : (
          <>
            <span className="loading-ring" aria-hidden="true" />
            <strong>{copy.loadingContent}</strong>
          </>
        )}
      </div>
    );
  }

  if (guidedTrial.state.status === "active") {
    if (guidedTrial.state.phase === "complete") {
      return (
        <div className="web-app" dir={direction} lang={setup.supportLanguage}>
          <GuidedTrialCompletion
            setup={setup}
            appearance={appearance}
            onAppearance={setAppearance}
            onContinue={() => {
              guidedTrial.finish();
              setRoute("home");
            }}
          />
        </div>
      );
    }
    if (!guidedTrial.state.started) {
      return (
        <div className="web-app" dir={direction} lang={setup.supportLanguage}>
          <GuidedTrialGateway
            phase={guidedTrial.state.phase}
            setup={setup}
            appearance={appearance}
            onAppearance={setAppearance}
            onStart={guidedTrial.startPhase}
          />
        </div>
      );
    }
    return (
      <div className="web-app" dir={direction} lang={setup.supportLanguage}>
        <GuidedTrialStage
          key={guidedTrial.state.phase}
          phase={guidedTrial.state.phase}
          scenario={tutorialScenario as ScenarioPackage}
          setup={setup}
          onComplete={guidedTrial.completePhase}
        />
      </div>
    );
  }

  const scenario = library.activeScenario?.scenario || null;
  const learningReady = Boolean(scenario);
  const mixedReady = library.installedScenarios.some((installed) => (
    (installed.scenario.taskStructure.simulationObjectives || []).length > 0
  ));
  const mobileCopy = getCopy(setup.supportLanguage);
  const navigateRoute = (nextRoute: AppRoute) => {
    if (!scenario && nextRoute !== "home" && nextRoute !== "profile" && nextRoute !== "mixed") {
      setRequestedMode(nextRoute);
      if (route !== "home") {
        setRoute("home");
      }
      return;
    }
    if (nextRoute === "home" || nextRoute === "profile" || nextRoute === "mixed") {
      setRoute(nextRoute);
      return;
    }
    if (!scenario) return;
    const request = buildScenarioRouteAudioRequest(scenario, nextRoute);
    if (!request) {
      setRoute(nextRoute);
      return;
    }
    void audioAccess.prepareUnit(request).then((granted) => {
      if (granted) setRoute(nextRoute);
    });
  };
  const changeScenario = () => {
    setRequestedMode(null);
    library.openScenarioPicker();
    setRoute("home");
    void interstitials.maybeShow("scenarioExit");
  };
  const chooseLevel = () => {
    setRequestedMode(null);
    library.openLevelPicker();
    setRoute("home");
  };
  const chooseDomain = () => {
    setRequestedMode(null);
    library.openDomainPicker();
    setRoute("home");
  };
  if (editingSetup) {
    const leaveLanguageEditor = (action?: () => void) => {
      setEditingSetup(false);
      action?.();
    };
    return (
      <div
        className={`web-app app-workspace ${premiumExperienceClassName}`.trim()}
        dir={direction}
        lang={setup.supportLanguage}
      >
        <AppNavigation
          route={route}
          onRoute={(nextRoute) => leaveLanguageEditor(() => navigateRoute(nextRoute))}
          onChangeLanguages={() => undefined}
          onChooseLevel={() => leaveLanguageEditor(chooseLevel)}
          onChooseDomain={() => leaveLanguageEditor(chooseDomain)}
          onChangeScenario={() => leaveLanguageEditor(changeScenario)}
          onOpenCurrentScenario={() => leaveLanguageEditor(() => setRoute("home"))}
          setup={setup}
          selectedLevel={library.selection.level}
          selectedDomain={library.selection.domain}
          currentScenarioTitle={scenario?.title || null}
          learningReady={learningReady}
          mixedReady={mixedReady}
        />
        <div className="workspace-main">
          <SetupFlow
            initialSetup={setup}
            appearance={appearance}
            onAppearance={setAppearance}
            onCancel={() => setEditingSetup(false)}
            onComplete={(value) => { setSetup(value); setEditingSetup(false); setRoute("home"); }}
          />
        </div>
      </div>
    );
  }
  const guide = (() => {
    let guideId: WebContextGuideId;
    let title: string;
    let steps: readonly { title: string; body: string }[];
    let selectors: string[];
    if (route === "home" && !scenario) {
      guideId = "domain_path";
      title = mobileCopy.contextGuideDomainTitle;
      steps = mobileCopy.contextGuideDomainSteps;
      selectors = [".level-grid .level-card:nth-child(1)", ".level-grid .level-card:nth-child(2)", ".level-grid .level-card:nth-child(3)"];
    } else if (route === "home") {
      guideId = "mode_path";
      title = mobileCopy.contextGuideModeTitle;
      steps = mobileCopy.contextGuideModeSteps;
      selectors = [".mode-card:nth-child(1)", ".mode-card:nth-child(2)", ".mode-card:nth-child(3)"];
    } else if (route === "practice") {
      guideId = "practice_intro";
      title = mobileCopy.contextGuidePracticeTitle;
      steps = mobileCopy.contextGuidePracticeSteps;
      selectors = [".lesson-topline", ".lesson-stage", ".speech-button"];
    } else if (route === "vocabulary") {
      guideId = "vocabulary_intro";
      title = mobileCopy.contextGuideVocabularyTitle;
      steps = mobileCopy.contextGuideVocabularySteps;
      selectors = [".lesson-stage", ".segmented-control", ".segmented-control"];
    } else if (route === "missions") {
      guideId = "missions_intro";
      title = mobileCopy.contextGuideMissionsTitle;
      steps = mobileCopy.contextGuideMissionsSteps;
      selectors = [".lesson-topline", ".mission-card", ".speech-button"];
    } else if (route === "mixed" && mixedReady) {
      guideId = "mixed_missions_intro";
      title = mobileCopy.contextGuideMixedMissionsTitle;
      steps = mobileCopy.contextGuideMixedMissionsSteps;
      selectors = [".mixed-summary", ".mixed-filter-row", ".mixed-picker-action"];
    } else {
      return null;
    }
    return contextGuides.dismissed[guideId] ? null : { guideId, selectors, steps, title };
  })();

  return (
    <div
      className={`web-app app-workspace ${premiumExperienceClassName}`.trim()}
      dir={direction}
      lang={setup.supportLanguage}
    >
      <AppNavigation
        route={route}
        onRoute={navigateRoute}
        onChangeLanguages={() => setEditingSetup(true)}
        onChooseLevel={chooseLevel}
        onChooseDomain={chooseDomain}
        onChangeScenario={changeScenario}
        onOpenCurrentScenario={() => setRoute("home")}
        setup={setup}
        selectedLevel={library.selection.level}
        selectedDomain={library.selection.domain}
        currentScenarioTitle={scenario?.title || null}
        learningReady={learningReady}
        mixedReady={mixedReady}
      />
      <div className="workspace-main">
        <AppHeader
          setup={setup}
          route={route}
          appearance={appearance}
          onAppearance={setAppearance}
          onBack={() => setRoute("home")}
          onMixed={() => navigateRoute("mixed")}
          onChangeLanguages={() => setEditingSetup(true)}
          learningReady={learningReady}
          mixedReady={mixedReady}
          showAppearance={route === "home"}
        />
        <main className="workspace-content">
          {route === "home" && scenario ? (
            <HomeView
              scenario={scenario}
              setup={setup}
              onRoute={navigateRoute}
              onChangeScenario={changeScenario}
              progress={progress}
              rewardedAccess={monetization.profile.subscriptionTier !== "premium" && monetization.profile.adsEnabled}
            />
          ) : null}
          {route === "home" && !scenario ? <ContentLibraryView setup={setup} library={library} progressByLevel={progressByLevel} requestedMode={requestedMode} /> : null}
          {(route === "practice" || route === "vocabulary" || route === "missions") && scenario ? (
            <AudioReadyBoundary key={`${scenario.scenarioId}:${route}`} request={routeAudioRequest} language={setup.supportLanguage}
              isUnitReady={audioAccess.isUnitReady} prepareUnit={audioAccess.prepareUnit}
              onBack={() => setRoute("home")} backLabel={WEB_COPY[setup.supportLanguage].home}>
              {route === "practice" ? <PracticeView scenario={scenario} setup={setup} progress={progress} audioAccess={audioAccess} onOpenMissions={() => navigateRoute("missions")} onNaturalBreak={(event) => void interstitials.maybeShow(event)} /> : null}
              {route === "vocabulary" ? <VocabularyView scenario={scenario} setup={setup} progress={progress} audioAccess={audioAccess} onNaturalBreak={(event) => void interstitials.maybeShow(event)} /> : null}
              {route === "missions" ? <MissionsView scenario={scenario} setup={setup} progress={progress} audioAccess={audioAccess} onNaturalBreak={(event) => void interstitials.maybeShow(event)} onOpenHome={() => navigateRoute("home")} /> : null}
            </AudioReadyBoundary>
          ) : null}
          {route === "mixed" ? (
            <MixedMissionsView
              scenario={scenario}
              setup={setup}
              progress={progress}
              audioAccess={audioAccess}
              installedScenarios={library.installedScenarios}
              loading={library.installedStatus === "loading"}
              progressByLevel={progressByLevel}
              onNaturalBreak={(event) => void interstitials.maybeShow(event)}
            />
          ) : null}
          {route === "profile" ? (
            <ProfileView
              setup={setup}
              appearance={appearance}
              onAppearance={setAppearance}
              onResetSetup={() => setSetup(null)}
              onChangeLanguages={() => setEditingSetup(true)}
              auth={auth}
              monetization={monetization}
              reminders={reminders}
              onRestartGuides={() => { contextGuides.restart(); setRoute("home"); }}
            />
          ) : null}
        </main>
      </div>
      {audioAccess.dialog}
      {guide ? (
        <ContextGuideOverlay
          guideId={guide.guideId}
          selectors={guide.selectors}
          steps={guide.steps}
          title={guide.title}
          stepLabel={mobileCopy.firstUseGuideStepLabel}
          skipLabel={mobileCopy.firstUseGuideSkip}
          nextLabel={mobileCopy.firstUseGuideNext}
          doneLabel={mobileCopy.firstUseGuideDone}
          onDismiss={() => contextGuides.dismiss(guide.guideId)}
        />
      ) : null}
    </div>
  );
}
